import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, IconControl, Loading, SidePanelFormControl } from "spark-core-dx/components";
import { AuthManager } from "spark-core-dx/managers";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  buildDeploymentStepValues,
  buildDestinationDeploymentSavePayload,
  getDeploymentRecordId,
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationSetupDefaults,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeDeploymentRow,
  saveDestination
} from "./destinationSetupStepUtils";
import {
  asArray,
  buildResourceOperationQueryParams,
  buildWizardValidationError,
  copyTextToClipboard,
  getErrorSummary,
  getOperationItem,
  getOperationItems,
  getOperationResult,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";
import { sanitizeHtmlFragment } from "../../../../../utilities/markupSanitizer";

const AUTH_MODE_OAUTH_TOKEN = 1;
const AUTH_MODE_ANONYMOUS = 3;
const AUTH_MODE_BASIC = 4;
const AUTH_MODE_OAUTH_AND_BASIC = 5;
const MCP_AUTH_MODE_SELECTED_PROFILES = 1;
const MCP_AUTH_MODE_ALL_AUTHENTICATED_USERS = 2;

const AUTH_STATUS_SKIPPED = "Skipped";
const AUTH_STATUS_CONFIGURED = "Configured";
const AUTH_STATUS_TEST_PASSED = "Test passed";
const AUTH_STATUS_TEST_FAILED = "Test failed";
const CREDENTIAL_TEST_STATUS_PENDING = "Pending";
const CREDENTIAL_TEST_STATUS_SUCCESS = "Success";
const CREDENTIAL_TEST_STATUS_FAILED = "Failed";
const EMPTY_TEST_CREDENTIALS = {
  PatTestCredential: "",
  BasicTestClientKey: "",
  BasicTestClientSecret: "",
  McpTestCredential: ""
};
const AUTH_TAB_SETUP = "setup";
const AUTH_TAB_TEST = "test";
const AUTH_TAB_STORAGE_PREFIX = "convoke:endpoint-auth-tab";

const normalizeAuthTab = (value) => value === AUTH_TAB_TEST ? AUTH_TAB_TEST : AUTH_TAB_SETUP;

const getAuthTabStorageKey = ({ endpointId, deploymentId }) => {
  if (!endpointId || !deploymentId) return "";
  return `${AUTH_TAB_STORAGE_PREFIX}:${endpointId}:${deploymentId}`;
};

const readStoredAuthTab = (storageKey) => {
  if (!storageKey || typeof window === "undefined") return "";
  try {
    const storedTab = window.sessionStorage?.getItem(storageKey);
    return storedTab ? normalizeAuthTab(storedTab) : "";
  } catch (ex) {
    return "";
  }
};

const writeStoredAuthTab = (storageKey, tab) => {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.sessionStorage?.setItem(storageKey, normalizeAuthTab(tab));
  } catch (ex) {
    // Session storage can be blocked by browser settings; tab persistence is best effort.
  }
};

const getCurrentLoggedInAccessToken = () => {
  try {
    const authManagerToken = String(AuthManager?.getAccessToken?.() || AuthManager?.getTokens?.()?.Access || "").trim();
    if (authManagerToken) return authManagerToken;
    if (typeof window === "undefined") return "";
    const storedTokens = JSON.parse(window.sessionStorage?.getItem("CoreAuth") || "null");
    return String(storedTokens?.Access || "").trim();
  } catch (ex) {
    return "";
  }
};

const getCurrentBrowserOrigin = () => {
  try {
    return typeof window === "undefined" ? "" : String(window.location?.origin || "").trim();
  } catch (ex) {
    return "";
  }
};

const option = (text, value, sortOrder) => ({
  Text: text,
  Value: String(value),
  ...(sortOrder === undefined ? {} : { SortOrder: sortOrder })
});

const fieldValue = (nextValue) => {
  if (nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)) {
    return readValue(nextValue, "Value", "value", "Text", "text") ?? "";
  }
  return nextValue ?? "";
};

const getCredentialTestStatusClass = (status) => {
  if (status === CREDENTIAL_TEST_STATUS_SUCCESS) return "success";
  if (status === CREDENTIAL_TEST_STATUS_FAILED) return "failed";
  return "pending";
};

const buildField = ({
  fieldName,
  displayName,
  description = "",
  fieldType = SystemTypes.UIFieldType.TextBox,
  options = [],
  isRequired = false
}) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  Description: description,
  FieldType: fieldType,
  DataJson: options,
  IsRequired: isRequired,
  ValidationType: SystemTypes.UIFieldValidationType.None
});

const AUTH_MODE_OPTIONS = [
  option("PAT Token", AUTH_MODE_OAUTH_TOKEN),
  option("Basic (Client + Secret)", AUTH_MODE_BASIC),
  option("Anonymous", AUTH_MODE_ANONYMOUS),
  option("PAT + Basic", AUTH_MODE_OAUTH_AND_BASIC)
];

const MCP_IDENTITY_MODE_OPTIONS = [
  option("Service Account", 1),
  option("User Context", 2),
  option("Hybrid", 3)
];

const MCP_AUTHORIZATION_MODE_OPTIONS = [
  option("Selected profiles", MCP_AUTH_MODE_SELECTED_PROFILES),
  option("All profiles", MCP_AUTH_MODE_ALL_AUTHENTICATED_USERS)
];

const FIELDS = {
  authMode: buildField({
    fieldName: "ApiAuthenticationMode",
    displayName: "API Auth Mode",
    description: "Controls authentication for hosted API data routes.",
    fieldType: SystemTypes.UIFieldType.SelectList,
    options: AUTH_MODE_OPTIONS,
    isRequired: true
  }),
  basicScope: buildField({
    fieldName: "APIBasicAuthScope",
    displayName: "Basic Auth Scope",
    description: "OAuth client-credentials scope used to exchange the client id and secret for a token, such as https://your-app-domain/.default. Do not paste the client secret here."
  }),
  azureClientKey: buildField({
    fieldName: "AzureClientKey",
    displayName: "Azure Client Key",
    description: "Azure application client id issued for this API caller."
  }),
  friendlyClientName: buildField({
    fieldName: "FriendlyClientName",
    displayName: "Client Name",
    description: "Friendly service profile name for this API client."
  }),
  mcpIdentityMode: buildField({
    fieldName: "McpExecutionIdentityMode",
    displayName: "MCP Identity Mode",
    description: "Execution identity used by deployment-scoped MCP tools.",
    fieldType: SystemTypes.UIFieldType.SelectList,
    options: MCP_IDENTITY_MODE_OPTIONS
  }),
  mcpAuthorizationMode: buildField({
    fieldName: "McpAuthorizationMode",
    displayName: "MCP Access",
    description: "Controls which authenticated user profiles can access this MCP deployment.",
    fieldType: SystemTypes.UIFieldType.SelectList,
    options: MCP_AUTHORIZATION_MODE_OPTIONS,
    isRequired: true
  }),
  mcpAuthorizedProfiles: buildField({
    fieldName: "McpAuthorizedProfileIds",
    displayName: "Authorized Profiles",
    description: "User profiles allowed to connect to this MCP server.",
    fieldType: SystemTypes.UIFieldType.MultiSelectList,
    options: []
  }),
  testCredential: buildField({
    fieldName: "TestCredential",
    displayName: "Credential",
    description: "PAT bearer token, Basic header, or MCP OAuth token used only for this test."
  }),
  patTestCredential: buildField({
    fieldName: "PatTestCredential",
    displayName: "PAT Token",
    description: "Paste a PAT created for this endpoint, then select Test PAT."
  }),
  basicTestClientKey: buildField({
    fieldName: "BasicTestClientKey",
    displayName: "Client Key",
    description: "Azure application client key used only for this Basic auth test."
  }),
  basicTestClientSecret: buildField({
    fieldName: "BasicTestClientSecret",
    displayName: "Client Secret",
    description: "Azure client secret used only for this Basic auth test."
  }),
  mcpTestCredential: buildField({
    fieldName: "McpTestCredential",
    displayName: "MCP OAuth Token",
    description: "MCP OAuth token used only for this test."
  }),
  tokenLabel: buildField({
    fieldName: "TokenLabel",
    displayName: "Token Label",
    isRequired: true
  }),
  tokenExpiration: buildField({
    fieldName: "ExpirationDuration",
    displayName: "Expiration",
    description: "PAT token lifetime. Maximum is 2 years.",
    fieldType: SystemTypes.UIFieldType.SelectList,
    options: [
      option("1 day", "1d", 1),
      option("3 days", "3d", 2),
      option("1 week", "1w", 3),
      option("1 month", "1m", 4),
      option("3 months", "3m", 5),
      option("6 months", "6m", 6),
      option("1 year", "1y", 7),
      option("2 years", "2y", 8)
    ],
    isRequired: true
  })
};

const BASIC_CLIENT_ACCESS_METADATA = {
  Name: "EndpointBasicClientAccess",
  SystemName: "EndpointBasicClientAccess",
  DisplayName: "Basic Client Access",
  Fields: [
    FIELDS.friendlyClientName,
    FIELDS.azureClientKey,
    FIELDS.basicScope
  ]
};

const getActiveDeployment = (deployments = []) =>
  asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true && readValue(deployment, "IsActive", "isActive") !== false)
  || asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
  || asArray(deployments)[0]
  || null;

const deploymentMatchesId = (deployment, deploymentId) => {
  const requestedId = String(deploymentId || "").trim();
  if (!requestedId || !deployment) return false;
  return [
    getDeploymentRecordId(deployment),
    readValue(deployment, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId"),
    readValue(deployment, "SchemaAPIDeploymentId", "schemaAPIDeploymentId", "schemaApiDeploymentId"),
    readValue(deployment, "DeploymentId", "deploymentId"),
    readValue(deployment, "UniqueId", "uniqueId"),
    readValue(deployment, "_id", "id", "Id")
  ].some((candidate) => String(candidate || "").trim() === requestedId);
};

const getDeploymentOptionLabel = (deployment, index = 0) =>
  readValue(deployment, "DisplayName", "displayName", "Name", "name")
  || readValue(deployment, "Key", "key", "DeploymentRouteName", "deploymentRouteName", "SystemName", "systemName")
  || `Deployment ${index + 1}`;

const hasDateValue = (value) => {
  if (value === undefined || value === null) return false;
  const text = String(value).trim();
  if (!text || text.startsWith("0001-01-01")) return false;
  return true;
};

const isExpired = (token) => {
  const expiration = readValue(token, "ExpirationDate", "expirationDate");
  if (!hasDateValue(expiration)) return false;
  const parsed = new Date(expiration);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() <= Date.now();
};

const isActiveToken = (token) =>
  readValue(token, "IsDeleted", "isDeleted") !== true
  && readValue(token, "IsActive", "isActive") !== false
  && !hasDateValue(readValue(token, "RevokedDate", "revokedDate"))
  && !isExpired(token);

const isActiveBasicClientAccess = (access) =>
  Boolean(String(readValue(access, "AzureClientKey", "azureClientKey") || "").trim())
  && readValue(access, "IsDeleted", "isDeleted") !== true
  && readValue(access, "IsActive", "isActive") !== false;

const getTokenStatusLabel = (token) => {
  if (isActiveToken(token)) return "Active";
  if (hasDateValue(readValue(token, "RevokedDate", "revokedDate"))) return "Revoked";
  if (isExpired(token)) return "Expired";
  return "Inactive";
};

const getTokenCreatedTime = (token) => {
  const created = readValue(token, "CreatedDate", "createdDate", "CreateDate", "createDate");
  if (!hasDateValue(created)) return 0;
  const parsed = new Date(created);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const sortTokensByCreatedDescending = (tokens) =>
  [...tokens].sort((left, right) => getTokenCreatedTime(right) - getTokenCreatedTime(left));

const sortTokensForDisplay = (tokens) => {
  const tokenList = asArray(tokens);
  const activeTokens = sortTokensByCreatedDescending(tokenList.filter(isActiveToken));
  const inactiveTokens = sortTokensByCreatedDescending(tokenList.filter((token) => !isActiveToken(token)));
  return [...activeTokens, ...inactiveTokens];
};

const getTokenRowTitle = (token) => {
  const label = readValue(token, "TokenLabel", "tokenLabel") || "PAT Token";
  return `${isActiveToken(token) ? "Current PAT Token" : "PAT Token History"}: ${label}`;
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const getTokenId = (token) => readValue(token, "UniqueId", "uniqueId", "_id", "id", "Id");

const getDeploymentAuthMode = (deployment) => {
  const value = Number(readValue(deployment, "ApiAuthenticationMode", "apiAuthenticationMode"));
  return value || AUTH_MODE_OAUTH_TOKEN;
};

const isMcpSurfaceEnabled = (deployment) => {
  const rawSurfaceMode = readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode");
  const surfaceMode = Number(rawSurfaceMode);
  if (Number.isFinite(surfaceMode) && surfaceMode > 0) {
    return surfaceMode === 2 || surfaceMode === 3;
  }

  const normalizedSurfaceMode = String(rawSurfaceMode || "").replace(/\s+/g, "").toLowerCase();
  if (normalizedSurfaceMode === "mcponly" || normalizedSurfaceMode === "both" || normalizedSurfaceMode === "apiandmcp") {
    return true;
  }

  const mcpEnabledValue = readValue(deployment, "McpEnabled", "mcpEnabled");
  return mcpEnabledValue === true || String(mcpEnabledValue).trim().toLowerCase() === "true";
};

const isHostedApiSurfaceEnabled = (deployment) => {
  const rawSurfaceMode = readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode");
  const surfaceMode = Number(rawSurfaceMode);
  if (Number.isFinite(surfaceMode) && surfaceMode > 0) {
    return surfaceMode === 1 || surfaceMode === 3;
  }

  const normalizedSurfaceMode = String(rawSurfaceMode || "").replace(/\s+/g, "").toLowerCase();
  if (normalizedSurfaceMode === "mcponly") return false;
  if (normalizedSurfaceMode === "apionly" || normalizedSurfaceMode === "both" || normalizedSurfaceMode === "apiandmcp") return true;

  const hostedApiEnabledValue = readValue(deployment, "HostedApiEnabled", "hostedApiEnabled");
  if (hostedApiEnabledValue === false || String(hostedApiEnabledValue).trim().toLowerCase() === "false") return false;

  return true;
};

const getMcpIdentityMode = (deployment) =>
  isMcpSurfaceEnabled(deployment) && !isHostedApiSurfaceEnabled(deployment)
    ? 2
    : Number(readValue(deployment, "McpExecutionIdentityMode", "mcpExecutionIdentityMode")) || 1;

const isBasicAuthMode = (mode) => {
  const numericMode = Number(mode) || AUTH_MODE_OAUTH_TOKEN;
  return numericMode === AUTH_MODE_BASIC || numericMode === AUTH_MODE_OAUTH_AND_BASIC;
};

const getAuthModeDetails = (mode) => {
  const numericMode = Number(mode) || AUTH_MODE_OAUTH_TOKEN;
  if (numericMode === AUTH_MODE_BASIC) {
    return {
      title: "Basic (Client + Secret)",
      status: "Basic only",
      summary: "Choose this for third-party systems that do not sign in to Convoke. An administrator issues an Azure client key and secret, and Convoke maps that client to a service profile.",
      notes: [
        "Request a client key and secret from the administrator.",
        "Convoke stores the client key and endpoint permission, but never stores the client secret.",
        "Callers send Authorization: Basic base64(clientKey:clientSecret)."
      ]
    };
  }
  if (numericMode === AUTH_MODE_OAUTH_AND_BASIC) {
    return {
      title: "PAT Authentication",
      status: "Configured",
      summary: "Use a Personal Access Token (PAT) when API calls should run as the currently signed-in Convoke user from code or scripts. Basic client-key access is configured in the Basic Authentication section.",
      notes: [
        "Personal Access Token (PAT) credentials let a Convoke user call the API as themselves from code or scripts.",
        "Basic auth lets external systems call the API with an administrator-issued Azure client key and secret.",
        "Both paths still require the endpoint permission.",
        "Use the copied PAT secret exactly as returned. It already starts with API- and contains the encoded endpoint permission."
      ]
    };
  }
  if (numericMode === AUTH_MODE_ANONYMOUS) {
    return {
      title: "Anonymous",
      status: "No credentials",
      summary: "API data routes can be called without PAT tokens or Basic credentials. Route, client, deployment, and scope validation still apply.",
      notes: [
        "Anonymous is not the default and should only be used for intentionally public API routes.",
        "Anonymous mode applies to API routes only. MCP remains OAuth based.",
        "Save & Continue shows a confirmation warning before this mode is accepted."
      ]
    };
  }
  return {
    title: "PAT Authentication",
    status: "Configured",
    summary: "Use a Personal Access Token (PAT) when API calls should run as the currently logged-in Convoke user from code or scripts.",
    notes: [
      "Personal Access Token (PAT) credentials authenticate as the creating Convoke user.",
      "PAT tokens are scoped to this deployment permission and cannot sign in to the app.",
      "Use the copied PAT secret exactly as returned. It already starts with API- and contains the encoded endpoint permission."
    ]
  };
};

const deriveAuthTestType = (deployment, authMode, credential) => {
  if (Number(authMode) === AUTH_MODE_ANONYMOUS) return "Anonymous";
  if (Number(authMode) === AUTH_MODE_BASIC) return "BasicAuth";
  const publicSurfaceMode = Number(readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (publicSurfaceMode === 2) return "McpOAuth";
  if (String(credential || "").trim().toLowerCase().startsWith("basic ")) return "BasicAuth";
  return "BearerToken";
};

const buildBasicAuthorizationHeader = (clientKey, clientSecret) => {
  const key = String(clientKey || "").trim();
  const secret = String(clientSecret || "");
  if (!key || !secret) return "";
  return `Basic ${window.btoa(`${key}:${secret}`)}`;
};

const getHostedApiBaseUri = () => {
  if (typeof window === "undefined") return "";
  const config = window.configData || window.config || {};
  const apiUris = config.API_URIS || config.apiUris || {};
  const convokeUri = getFirstTextValue(
    apiUris.CONVOKE,
    apiUris.Convoke,
    apiUris.convoke,
    config.CONVOKE_API_URI,
    config.convokeApiUri
  );
  return convokeUri.replace(/\/api\/Convoke\/?$/i, "").replace(/\/+$/, "");
};

const buildEndpointAuthTestUrl = (authContext) => {
  const route = String(authContext?.authTestRoute || "").trim();
  if (!route) return "";
  const baseUri = getHostedApiBaseUri();
  return baseUri ? `${baseUri}${route.startsWith("/") ? route : `/${route}`}` : "";
};

const buildAuthorizationHeaderForTest = (authType, credential) => {
  const text = String(credential || "").trim();
  if (!text) return "";
  if (String(authType || "").toLowerCase() === "basicauth") {
    return text.toLowerCase().startsWith("basic ") ? text : `Basic ${text}`;
  }
  if (String(authType || "").toLowerCase() === "bearertoken") {
    return text.toLowerCase().startsWith("bearer ") ? text : `Bearer ${text}`;
  }
  return text;
};

const readEndpointAuthTestResponse = async (response) => {
  const rawText = await response.text();
  const payload = parseMaybeJson(rawText) || {
    success: response.ok,
    message: rawText || response.statusText || `HTTP ${response.status}`
  };

  if (!response.ok) {
    const title = readValue(payload, "title", "Title") || "Authentication test failed";
    const detail = readValue(payload, "detail", "Detail", "message", "Message") || response.statusText || `HTTP ${response.status}`;
    throw new Error(`${title}: ${detail}`);
  }

  return payload;
};

const safeJson = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return JSON.stringify(value ?? {}, null, 2);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const isRouteSafe = (value) => /^[A-Za-z0-9_-]+$/.test(String(value || ""));

const getRouteSafeValue = (...values) =>
  values
    .map((value) => String(value || "").trim())
    .find((value) => value && isRouteSafe(value)) || "";

const getFirstTextValue = (...values) =>
  values
    .map((value) => String(value || "").trim())
    .find(Boolean) || "";

const formatList = (items) => {
  const values = asArray(items).filter(Boolean);
  if (values.length <= 1) return values[0] || "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
};

const normalizeBasicAppDisplayPart = (value) => String(value || "")
  .replace(/[-_]+/g, " ")
  .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/\s+/g, " ")
  .trim()
  .split(" ")
  .filter(Boolean)
  .map((word) => (/^[A-Z0-9]+$/.test(word) ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`))
  .join(" ");

const getProfileAuthProviderTenantId = (...sources) => {
  for (const source of sources) {
    const authProviders = asArray(readValue(source, "AuthProviderOptions", "authProviderOptions"));
    const tenantId = getFirstTextValue(...authProviders.map((provider) => readValue(provider, "TenantId", "tenantId")));
    if (tenantId) return tenantId;
  }
  return "";
};

const resolveClientShortCode = ({ coreContext } = {}) =>
  getRouteSafeValue(readValue(coreContext, "ClientShortCode", "clientShortCode"));

const normalizeBasicAuthScopeRecommendation = (value) => {
  const text = getFirstTextValue(value).trim().replace(/\/+$/, "");
  if (!text || text === "https:" || text === "http:" || text === "https://" || text === "http://") return "";
  if (text.toLowerCase().endsWith("/.default")) return text;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) return `${text}/.default`;
  if (/^(api|https?):\/\//i.test(text)) return `${text}/.default`;
  return "";
};

const getAuthProviderOptionValues = (source) =>
  asArray(readValue(source, "AuthProviderOptions", "authProviderOptions"))
    .flatMap((optionItem) => [
      readValue(optionItem, "AuthProviderClientId", "authProviderClientId"),
      readValue(optionItem, "ScopeIdUri", "scopeIdUri"),
      readValue(optionItem, "ScopeDomainURI", "scopeDomainURI", "scopeDomainUri"),
      readValue(optionItem, "AppDomain", "appDomain")
    ]);

const resolveRecommendedBasicAuthScope = ({ coreContext, profile } = {}) => {
  const candidates = [
    readValue(coreContext, "AZEntraIDAppClientId", "azEntraIDAppClientId"),
    readValue(coreContext?.Config, "AZEntraIDAppClientId", "azEntraIDAppClientId"),
    readValue(coreContext?.Profile, "AZEntraIDAppClientId", "azEntraIDAppClientId"),
    readValue(profile, "AZEntraIDAppClientId", "azEntraIDAppClientId"),
    ...getAuthProviderOptionValues(coreContext),
    ...getAuthProviderOptionValues(coreContext?.Profile),
    ...getAuthProviderOptionValues(profile),
    readValue(coreContext, "ScopeDomainURI", "scopeDomainURI", "scopeDomainUri"),
    readValue(coreContext?.Config, "ScopeDomainURI", "scopeDomainURI", "scopeDomainUri"),
    readValue(coreContext?.Profile, "ScopeDomainURI", "scopeDomainURI", "scopeDomainUri"),
    readValue(profile, "ScopeDomainURI", "scopeDomainURI", "scopeDomainUri")
  ];

  return candidates
    .map(normalizeBasicAuthScopeRecommendation)
    .find(Boolean) || "";
};

const isLikelyWrongBasicAuthScope = (value) => {
  const text = getFirstTextValue(value).trim();
  return Boolean(text && (text.toUpperCase().startsWith("API-") || text.includes("~")));
};

const getBasicAuthScopeInputError = (value, recommendedScope = "") => {
  const text = getFirstTextValue(value).trim();
  const example = recommendedScope || "https://your-app-domain/.default";
  if (!text) return "Basic Auth Scope is required.";
  if (text.toUpperCase().startsWith("API-")) return `Basic Auth Scope must be the OAuth token scope, not the Convoke endpoint permission. Use a value like ${example}.`;
  if (text.includes("~")) return `Basic Auth Scope is not the client secret. Paste the secret only in the Client Secret test field. Use a value like ${example}.`;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) return `Basic Auth Scope must end with /.default. Use ${text}/.default.`;
  if (/^(api|https?):\/\//i.test(text) && !text.toLowerCase().endsWith("/.default")) return `Basic Auth Scope must end with /.default. Use a value like ${example}.`;
  return "";
};

const resolveEffectiveBasicAuthScope = (value, recommendedScope = "") => {
  const text = getFirstTextValue(value).trim();
  if (!text || isLikelyWrongBasicAuthScope(text)) return recommendedScope || text;
  return normalizeBasicAuthScopeRecommendation(text) || text;
};


const resolveEndpointAuthContext = ({ coreContext, profile, client, endpoint, deployment, wizard } = {}) => {
  const currentTenantId = getFirstTextValue(
    readValue(coreContext, "CurrentTenantId", "currentTenantId"),
    readValue(coreContext?.Profile, "CurrentTenantId", "currentTenantId"),
    readValue(coreContext?.Config, "AZTenant", "azTenant", "AZTenantId", "azTenantId"),
    readValue(coreContext, "AZTenantId", "azTenantId", "AZTenant", "azTenant"),
    readValue(profile, "CurrentTenantId", "currentTenantId", "TenantId", "tenantId", "AZTenantId", "azTenantId", "AZTenant", "azTenant"),
    getProfileAuthProviderTenantId(coreContext, profile)
  );
  const clientShortCode = resolveClientShortCode({ coreContext, profile, client, endpoint, deployment, wizard });
  const deploymentKey = getRouteSafeValue(readValue(deployment, "Key", "key", "DeploymentRouteName", "deploymentRouteName"));
  const missingFields = [
    currentTenantId ? "" : "CurrentTenantId",
    clientShortCode ? "" : "ClientShortCode",
    deploymentKey ? "" : "deployment key"
  ].filter(Boolean);
  const errorMessage = missingFields.length
    ? `Endpoint authentication context is incomplete. Missing ${formatList(missingFields)}.`
    : "";
  const hasRouteContext = Boolean(clientShortCode && deploymentKey);
  const permissionKey = hasRouteContext ? ["API", clientShortCode, deploymentKey].join("-") : "";
  const recommendedBasicAuthScope = resolveRecommendedBasicAuthScope({ coreContext, profile });

  return {
    currentTenantId,
    clientShortCode,
    deploymentKey,
    permissionKey,
    recommendedBasicAuthScope,
    apiRouteExample: hasRouteContext ? ["/api", clientShortCode, "V1", deploymentKey, "..."].join("/") : "",
    authTestRoute: hasRouteContext ? ["/api", clientShortCode, "V1", deploymentKey, "auth-test"].join("/") : "",
    mcpRouteExample: hasRouteContext ? ["/mcp", clientShortCode, "V1", deploymentKey].join("/") : "",
    errorMessage
  };
};

const buildEndpointAuthExampleLines = (authMode, authContext) => {
  if (authContext?.errorMessage) return [];
  const routeLine = `GET ${authContext.apiRouteExample}`;
  const patLine = "Authorization: Bearer API-{encodedSecret}";
  const basicLine = "Authorization: Basic base64(clientKey:clientSecret)";

  if (Number(authMode) === AUTH_MODE_BASIC) return [routeLine, basicLine];
  if (Number(authMode) === AUTH_MODE_OAUTH_AND_BASIC) return [routeLine, patLine, basicLine];
  if (Number(authMode) === AUTH_MODE_ANONYMOUS) return [routeLine, "No Authorization header"];
  return [routeLine, patLine];
};

const parseMaybeJson = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const formatReadableValue = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "Not set";
  const text = String(value);
  const normalizedText = text.replace(/\s/g, "").toLowerCase();
  if (normalizedText === "mcpoauth") return "MCP OAuth";
  if (normalizedText === "oauth2userflow") return "OAuth 2 User Flow";
  if (normalizedText === "oauthandbasic") return "PAT + Basic";
  return text
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\bApi\b/g, "API")
    .replace(/\bMcp\b/g, "MCP")
    .replace(/\bPat\b/g, "PAT")
    .replace(/\bO\s*Auth\b/g, "OAuth")
    .replace(/\bOauth\b/g, "OAuth")
    .replace(/\bOAuth\s*2\b/g, "OAuth 2")
    .replace(/\s+/g, " ")
    .trim();
};

const buildFactGridHtml = (facts) => facts
  .filter(([, factValue]) => factValue !== null && factValue !== undefined && factValue !== "")
  .map(([label, factValue]) => `
    <div>
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(factValue)}</strong>
    </div>
  `).join("");

const buildAuthTestResultHtml = (value) => {
  const payload = parseMaybeJson(value);
  if (!payload) return "";

  const success = readValue(payload, "Success", "success");
  const message = readValue(payload, "Message", "message") || (success === false ? "Authentication test failed." : "Authentication test completed.");
  const authType = readValue(payload, "AuthType", "authType");
  const normalizedAuthType = String(authType || "").replace(/\s/g, "").toLowerCase();
  const isMcpOAuthResult = normalizedAuthType === "mcpoauth" || normalizedAuthType === "mcp";
  const facts = [
    ["Result", success === false ? "Failed" : "Passed"],
    ["Auth type", formatReadableValue(authType)],
    ["Authentication mode", formatReadableValue(readValue(payload, "AuthenticationMode", "authenticationMode"))],
    ["Authentication required", formatReadableValue(readValue(payload, "AuthenticationRequired", "authenticationRequired"))],
    ...(isMcpOAuthResult ? [] : [
      ["PAT enabled", formatReadableValue(readValue(payload, "ApiPatEnabled", "apiPatEnabled"))],
      ["Active PAT tokens", formatReadableValue(readValue(payload, "ActiveApiPatCount", "activeApiPatCount"))],
      ["Basic auth enabled", formatReadableValue(readValue(payload, "BasicAuthEnabled", "basicAuthEnabled"))],
      ["Basic client access", formatReadableValue(readValue(payload, "BasicClientAccessConfigured", "basicClientAccessConfigured"))],
      ["Anonymous access", formatReadableValue(readValue(payload, "AnonymousAccessEnabled", "anonymousAccessEnabled"))]
    ]),
    ["MCP enabled", formatReadableValue(readValue(payload, "McpEnabled", "mcpEnabled"))],
    ["MCP access", formatReadableValue(readValue(payload, "McpAuthorizationMode", "mcpAuthorizationMode"))],
    ["MCP authorized profiles", formatReadableValue(readValue(payload, "McpAuthorizedProfileCount", "mcpAuthorizedProfileCount"))],
    ["Permission", readValue(payload, "EndpointPermissionKey", "endpointPermissionKey")],
    ["Runtime role", formatReadableValue(readValue(payload, "RuntimeRole", "runtimeRole"))]
  ].filter(([, factValue]) => factValue !== null && factValue !== undefined && factValue !== "");

  const authenticatedUser = readValue(payload, "AuthenticatedUser", "authenticatedUser");
  const authProviderAccount = authenticatedUser ? readValue(authenticatedUser, "AuthProviderAccount", "authProviderAccount") : null;
  const endpointPermission = authenticatedUser ? readValue(authenticatedUser, "EndpointPermission", "endpointPermission") : null;
  const config = authenticatedUser ? readValue(authenticatedUser, "Config", "config") : null;
  const roles = authenticatedUser ? readValue(authenticatedUser, "Roles", "roles") : [];
  const roleText = Array.isArray(roles) && roles.length
    ? roles.map((role) => readValue(role, "Role", "role", "Name", "name")).filter(Boolean).join(", ")
    : "";
  const userFacts = authenticatedUser ? [
    ["User name", readValue(authenticatedUser, "UserName", "userName")],
    ["Display name", readValue(authenticatedUser, "DisplayName", "displayName")],
    ["Email", readValue(authenticatedUser, "Email", "email")],
    ["Active", formatReadableValue(readValue(authenticatedUser, "IsActive", "isActive"))],
    ["System admin", formatReadableValue(readValue(authenticatedUser, "IsSysAdmin", "isSysAdmin"))],
    ["Auth provider", authProviderAccount ? readValue(authProviderAccount, "AuthProvider", "authProvider") : ""],
    ["Auth client", authProviderAccount ? readValue(authProviderAccount, "AuthProviderClientId", "authProviderClientId") : ""],
    ["Permission count", formatReadableValue(readValue(authenticatedUser, "PermissionCount", "permissionCount"))],
    ["Endpoint can access", endpointPermission ? formatReadableValue(readValue(endpointPermission, "CanAccess", "canAccess")) : ""],
    ["Endpoint can execute", endpointPermission ? formatReadableValue(readValue(endpointPermission, "CanExecute", "canExecute")) : ""],
    ["Endpoint can view", endpointPermission ? formatReadableValue(readValue(endpointPermission, "CanView", "canView")) : ""],
    ["Default auth provider", config ? readValue(config, "DefaultAuthProvider", "defaultAuthProvider") : ""]
  ] : [];

  const factHtml = buildFactGridHtml(facts);
  const userFactHtml = buildFactGridHtml(userFacts);
  const roleHtml = roleText ? `
    <div class="api-auth-test-result__role-row">
      <span>Roles</span>
      <strong>${escapeHtml(roleText)}</strong>
    </div>
  ` : "";

  return sanitizeHtmlFragment(`
    <section class="api-auth-test-result ${success === false ? "api-auth-test-result--failed" : "api-auth-test-result--passed"}">
      <p>${escapeHtml(message)}</p>
      <div class="api-auth-test-result__facts">${factHtml}</div>
      ${userFactHtml || roleHtml ? `
        <h3>Authenticated User Context</h3>
        <div class="api-auth-test-result__facts api-auth-test-result__facts--user">${userFactHtml}${roleHtml}</div>
      ` : ""}
    </section>
  `);
};

const buildBasicClientAccessHelpHtml = ({
  endpoint,
  deployment,
  client,
  profile,
  form,
  wizard,
  coreContext
} = {}) => {
  const authContext = resolveEndpointAuthContext({ coreContext, profile, client, endpoint, deployment, wizard });
  const basicScope = getFirstTextValue(readValue(form, "APIBasicAuthScope", "apiBasicAuthScope"));
  const recommendedAppName = authContext.clientShortCode && authContext.deploymentKey
    ? `Convoke API Basic: ${normalizeBasicAppDisplayPart(authContext.clientShortCode)} ${normalizeBasicAppDisplayPart(authContext.deploymentKey)}`
    : "";
  const clientName = getFirstTextValue(readValue(form, "FriendlyClientName", "friendlyClientName")) || recommendedAppName;
  const basicScopeLooksWrong = isLikelyWrongBasicAuthScope(basicScope);
  const scopeText = basicScopeLooksWrong
    ? authContext.recommendedBasicAuthScope
    : (basicScope || authContext.recommendedBasicAuthScope);
  const scopeSource = basicScope && !basicScopeLooksWrong ? "configured Basic Auth Scope" : "recommended Basic Auth Scope";
  const contextWarningHtml = authContext.errorMessage
    ? `<div class="resource-wizard__connection-status failed">${escapeHtml(authContext.errorMessage)}</div>`
    : "";
  const scopeWarningHtml = basicScopeLooksWrong && authContext.recommendedBasicAuthScope
    ? `<div class="resource-wizard__connection-status failed">The saved Basic Auth Scope looks like a client secret or endpoint permission. Use the recommended OAuth scope below instead.</div>`
    : "";
  const factRows = [
    ["Client tenant", authContext.currentTenantId],
    ["Client short code", authContext.clientShortCode],
    ["Recommended Azure app name", recommendedAppName],
    ["Convoke client name", clientName],
    ["Endpoint permission", authContext.permissionKey],
    ["Basic Auth Scope", scopeText]
  ]
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `
        <div>
          <span>${escapeHtml(label)}</span>
          <strong>${escapeHtml(value)}</strong>
        </div>
      `)
    .join("");
  const tenantInstruction = authContext.currentTenantId && recommendedAppName
    ? `switch to tenant ${escapeHtml(authContext.currentTenantId)}, open App registrations, and create or select an application named ${escapeHtml(recommendedAppName)}.`
    : "switch to the Microsoft Entra tenant for this Convoke client, open App registrations, and create or select the application for this Basic client access.";
  const scopeInstruction = scopeText
    ? `Use the ${escapeHtml(scopeSource)} shown above as the token scope or audience expected by this environment.`
    : "Use the Basic Auth Scope configured for this endpoint as the token scope or audience expected by this environment.";
  const permissionInstruction = authContext.permissionKey
    ? `and grants ${escapeHtml(authContext.permissionKey)}.`
    : "and grants the endpoint permission once the profile context is available.";

  return sanitizeHtmlFragment(`
    <section class="api-auth-help">
      ${contextWarningHtml}
      ${scopeWarningHtml}
      <p>
        Basic client access uses a Microsoft Entra application client id and client secret. Convoke stores the client id,
        creates or maps a service profile, grants the endpoint permission, and never stores the secret.
      </p>
      ${factRows ? `<div class="api-auth-help__facts">${factRows}</div>` : ""}
      <p>
        Optional: use the Azure setup script download to automatically add or update the app registration and client
        secret in the tenant, or follow the manual steps below and do it yourself.
      </p>
      <ol>
        <li>
          <strong>Create or choose an app registration</strong>
          <span>In Microsoft Entra admin center, ${tenantInstruction}</span>
        </li>
        <li>
          <strong>Use service-credential settings</strong>
          <span>Use a single-tenant confidential client. Do not enable public client flows. A redirect URI is not required for this client-key and secret integration.</span>
        </li>
        <li>
          <strong>Create a client secret</strong>
          <span>Open Certificates &amp; secrets, add a client secret, and copy the secret value immediately. Azure will not show it again.</span>
        </li>
        <li>
          <strong>Confirm API scope and consent</strong>
          <span>${scopeInstruction} This is not the client secret and not the Convoke endpoint permission. Grant admin consent when the tenant requires it.</span>
        </li>
        <li>
          <strong>Save client access in Convoke</strong>
          <span>Enter the Client Name, Azure Client Key, and Basic Auth Scope. Convoke creates or maps the service profile ${permissionInstruction}</span>
        </li>
        <li>
          <strong>Call the endpoint</strong>
          <span>Send the application client id and client secret in the Basic authorization header from the calling system.</span>
        </li>
      </ol>
    </section>
  `);
};

const getResultDisplayValue = (result) =>
  readValue(getOperationResult(result), "Value", "value")
  || readValue(result, "Value", "value")
  || getOperationResult(result)
  || result;

const getBasicClientAccessDownloadState = ({ endpoint, endpointId, deployment } = {}) => {
  const deploymentId = getDeploymentRecordId(deployment);
  const apiId = endpointId || readValue(endpoint, "_id", "id", "UniqueId", "uniqueId");
  const disabled = !deploymentId || !apiId;
  return {
    deploymentId,
    apiId,
    disabled,
    disabledReason: disabled ? "Save the endpoint setup before downloading the script." : ""
  };
};

const getMcpOAuthSetupDownloadState = ({ endpoint, endpointId, deployment } = {}) => {
  const deploymentId = getDeploymentRecordId(deployment);
  const apiId = endpointId || readValue(endpoint, "_id", "id", "UniqueId", "uniqueId");
  const disabled = !deploymentId || !apiId;
  return {
    deploymentId,
    apiId,
    disabled,
    disabledReason: disabled ? "Save the endpoint setup before downloading the MCP OAuth setup script." : ""
  };
};

const extractPatTokenResult = (result) => {
  const value = getResultDisplayValue(result);
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      message: readValue(value, "message", "Message") || "PAT token created. You will not see this secret again.",
      tokenSecret: readValue(value, "tokenSecret", "TokenSecret", "secret", "Secret"),
      tokenLabel: readValue(value, "tokenLabel", "TokenLabel"),
      permissionKey: readValue(value, "permissionKey", "PermissionKey"),
      expirationDate: readValue(value, "expirationDate", "ExpirationDate"),
      tokenFingerprint: readValue(value, "tokenFingerprint", "TokenFingerprint")
    };
  }

  const text = typeof value === "string" ? value : "";
  const clipboardMatch = text.match(/<\|CLIPBOARD="([^"]+)"\|>/i);
  let tokenSecret = "";
  if (clipboardMatch?.[1]) {
    try {
      const normalized = clipboardMatch[1].replace(/-/g, "+").replace(/_/g, "/");
      tokenSecret = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
    } catch {
      tokenSecret = "";
    }
  }
  const displayHtml = text
    ? sanitizeHtmlFragment(text.replace(/<\|CLIPBOARD="[^"]+"\|>/ig, '<span class="api-token-created__copy-marker">Use Copy PAT Secret</span>'))
    : "";

  return {
    message: text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() || "PAT token created.",
    tokenSecret,
    html: displayHtml
  };
};

const normalizeProfileId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    return String(readValue(value, "ProfileId", "profileId", "Value", "value", "UniqueId", "uniqueId", "_id", "id") || "").trim();
  }
  return String(value).trim();
};

const getProfileId = (profile) =>
  normalizeProfileId(readValue(profile, "UniqueId", "uniqueId", "_id", "id", "ProfileId", "profileId"));

const getProfileDisplayName = (profile) =>
  readValue(profile, "Text", "text", "ProfileDisplayName", "profileDisplayName", "DisplayName", "displayName", "FullName", "fullName", "UserName", "userName", "Email", "email")
  || getProfileId(profile);

const normalizeProfileIds = (value) =>
  asArray(value)
    .map(normalizeProfileId)
    .filter(Boolean);

const getActiveMcpAuthorizedProfiles = (deployment) =>
  asArray(readValue(deployment, "McpAuthorizedProfiles", "mcpAuthorizedProfiles"))
    .filter((profile) => readValue(profile, "IsDeleted", "isDeleted") !== true && readValue(profile, "IsActive", "isActive") !== false);

const getMcpAuthorizedProfileIds = (deployment) =>
  getActiveMcpAuthorizedProfiles(deployment)
    .map((profile) => normalizeProfileId(readValue(profile, "ProfileId", "profileId")))
    .filter(Boolean);

const buildProfileOption = (profile) => {
  const profileId = getProfileId(profile);
  if (!profileId) return null;
  return {
    Text: getProfileDisplayName(profile),
    Value: profileId,
    Email: readValue(profile, "Email", "email", "ProfileEmail", "profileEmail") || "",
    UserName: readValue(profile, "UserName", "userName", "ProfileUserName", "profileUserName") || "",
    ClientId: readValue(profile, "ClientId", "clientId", "EndpointClientId", "endpointClientId") || ""
  };
};

const mergeProfileOptions = (...optionGroups) => {
  const map = new Map();
  optionGroups.flatMap((group) => asArray(group)).forEach((optionItem) => {
    const optionValue = normalizeProfileId(optionItem);
    if (!optionValue || map.has(optionValue)) return;
    const normalized = readValue(optionItem, "Text", "text")
      ? optionItem
      : buildProfileOption(optionItem);
    if (normalized) map.set(optionValue, normalized);
  });
  return Array.from(map.values()).sort((a, b) => String(a.Text || "").localeCompare(String(b.Text || "")));
};

const isBasicClientProfile = (profile) => {
  const userName = String(readValue(profile, "UserName", "userName", "ProfileUserName", "profileUserName") || "").trim().toLowerCase();
  const email = String(readValue(profile, "Email", "email", "ProfileEmail", "profileEmail") || "").trim().toLowerCase();
  return userName.startsWith("api-basic-client:")
    || email.endsWith("@api-client.local")
    || String(readValue(profile, "AuthProvider", "authProvider", "AuthProviderType", "authProviderType") || "").trim().toLowerCase() === "basic";
};

const buildMcpAuthorizedProfileRows = ({ deployment, selectedProfileIds, profileOptions, endpointClientId }) => {
  const selected = new Set(normalizeProfileIds(selectedProfileIds));
  const existingRows = asArray(readValue(deployment, "McpAuthorizedProfiles", "mcpAuthorizedProfiles"));
  const existingByProfileId = new Map(existingRows.map((row) => [normalizeProfileId(readValue(row, "ProfileId", "profileId")), row]));
  const optionByProfileId = new Map(asArray(profileOptions).map((profile) => [normalizeProfileId(profile), profile]));
  const rows = [];

  existingRows.forEach((row) => {
    const profileId = normalizeProfileId(readValue(row, "ProfileId", "profileId"));
    if (!profileId || selected.has(profileId)) return;
    rows.push({
      ...row,
      IsActive: false,
      IsDeleted: true
    });
  });

  selected.forEach((profileId) => {
    const existing = existingByProfileId.get(profileId) || {};
    const optionItem = optionByProfileId.get(profileId) || {};
    rows.push({
      ...existing,
      UniqueId: readValue(existing, "UniqueId", "uniqueId") || undefined,
      ProfileId: profileId,
      EndpointClientId: readValue(optionItem, "ClientId", "clientId") || endpointClientId || readValue(existing, "EndpointClientId", "endpointClientId") || "",
      ProfileDisplayName: readValue(optionItem, "Text", "text") || readValue(existing, "ProfileDisplayName", "profileDisplayName") || "",
      ProfileEmail: readValue(optionItem, "Email", "email") || readValue(existing, "ProfileEmail", "profileEmail") || "",
      ProfileUserName: readValue(optionItem, "UserName", "userName") || readValue(existing, "ProfileUserName", "profileUserName") || "",
      IsActive: true,
      IsDeleted: false
    });
  });

  return rows;
};

const EndpointAuthenticationConfiguratorStep = ({
  context,
  onChange,
  onContinue,
  registerStepController,
  managementMode = false,
  fixedDeploymentId = "",
  onSaved
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const currentProfile = coreContext?.Profile || coreContext?.profile || coreState?.Profile || coreState?.profile || null;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const endpointMgr = resourceDomainContext?.managers?.[managerKey];
  const profileMgr = resourceDomainContext?.managers?.profile;
  const endpointId = getDestinationIdFromValues(wizard, values, productResourceDefaults);
  const endpointLabel = getDestinationLabel(productResourceDefaults);
  const [endpoint, setEndpoint] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState(fixedDeploymentId || "");
  const [form, setForm] = useState(() => ({
    ApiAuthenticationMode: String(readValue(values, "ApiAuthenticationMode", "apiAuthenticationMode") || AUTH_MODE_OAUTH_TOKEN),
    APIBasicAuthScope: readValue(values, "APIBasicAuthScope", "apiBasicAuthScope") || "",
    AzureClientKey: "",
    FriendlyClientName: "",
    McpOAuthScopes: readValue(values, "McpOAuthScopes", "mcpOAuthScopes") || "",
    McpExecutionIdentityMode: String(readValue(values, "McpExecutionIdentityMode", "mcpExecutionIdentityMode") || 2),
    McpAuthorizationMode: String(readValue(values, "McpAuthorizationMode", "mcpAuthorizationMode") || MCP_AUTH_MODE_SELECTED_PROFILES),
    McpAuthorizedProfileIds: normalizeProfileIds(readValue(values, "McpAuthorizedProfileIds", "mcpAuthorizedProfileIds")),
    TestCredential: ""
  }));
  const [testCredentials, setTestCredentials] = useState(EMPTY_TEST_CREDENTIALS);
  const [profileOptions, setProfileOptions] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [loading, setLoading] = useState(Boolean(endpointId));
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState("");
  const [authStatus, setAuthStatus] = useState(readValue(values, "AuthenticationSetupStatus", "authenticationSetupStatus") || "Not configured");
  const [credentialTestStatus, setCredentialTestStatus] = useState(() => ({
    pat: readValue(values, "PatAuthenticationTestStatus", "patAuthenticationTestStatus") || CREDENTIAL_TEST_STATUS_PENDING,
    basic: readValue(values, "BasicAuthenticationTestStatus", "basicAuthenticationTestStatus") || CREDENTIAL_TEST_STATUS_PENDING
  }));
  const credentialTestStatusRef = useRef(credentialTestStatus);
  const [anonymousDialogOpen, setAnonymousDialogOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenForm, setTokenForm] = useState({ TokenLabel: "", ExpirationDuration: "1m" });
  const [resultDialog, setResultDialog] = useState({ open: false, title: "", content: "" });
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [activeAuthTab, setActiveAuthTab] = useState(AUTH_TAB_SETUP);
  const [basicAccessPanelOpen, setBasicAccessPanelOpen] = useState(false);
  const endpointMgrRef = useRef(endpointMgr);
  const profileMgrRef = useRef(profileMgr);
  const endpointRef = useRef(endpoint);
  const deploymentRef = useRef(deployment);
  const coreStateRef = useRef(coreState);
  const valuesRef = useRef(values);
  const busyRef = useRef(false);
  const loadKeyRef = useRef("");
  const anonymousContinueApprovedRef = useRef(false);
  const suppressSavedNotificationRef = useRef(false);

  endpointMgrRef.current = endpointMgr;
  profileMgrRef.current = profileMgr;
  endpointRef.current = endpoint;
  deploymentRef.current = deployment;
  coreStateRef.current = coreState;
  valuesRef.current = values;
  busyRef.current = loading || busy;
  credentialTestStatusRef.current = credentialTestStatus;
  const shouldNotifySaved = useCallback((options = {}) => options?.notify !== false && !suppressSavedNotificationRef.current, []);

  const endpointAuthContext = useMemo(() => resolveEndpointAuthContext({
    coreContext,
    profile: currentProfile,
    endpoint,
    deployment,
    wizard
  }), [coreContext, currentProfile, deployment, endpoint, wizard]);
  const basicAccessPanelScope = resolveEffectiveBasicAuthScope(form.APIBasicAuthScope, endpointAuthContext.recommendedBasicAuthScope);
  const deploymentRows = useMemo(() =>
    getDestinationDeploymentRows(endpoint, productResourceDefaults).map((row) =>
      normalizeDeploymentRow(row, endpoint, productResourceDefaults)
    ), [endpoint, productResourceDefaults]);
  const deploymentOptions = useMemo(() =>
    deploymentRows.map((row, index) => ({
      Text: getDeploymentOptionLabel(row, index),
      Value: getDeploymentRecordId(row) || String(index)
    })).filter((option) => option.Value), [deploymentRows]);
  const selectedDeploymentValue = fixedDeploymentId || getDeploymentRecordId(deployment) || selectedDeploymentId || deploymentOptions[0]?.Value || "";
  const authTabStorageKey = useMemo(() => getAuthTabStorageKey({
    endpointId,
    deploymentId: selectedDeploymentValue
  }), [endpointId, selectedDeploymentValue]);
  const deploymentSelectorField = useMemo(() => buildField({
    fieldName: "EndpointAuthenticationDeploymentId",
    displayName: "Deployment",
    description: "Select the endpoint deployment to configure and test.",
    fieldType: SystemTypes.UIFieldType.SelectList,
    options: deploymentOptions,
    isRequired: true
  }), [deploymentOptions]);
  const mcpEnabled = isMcpSurfaceEnabled(deployment);
  const hostedApiEnabled = isHostedApiSurfaceEnabled(deployment);
  const currentProfileOption = useMemo(() => buildProfileOption(currentProfile), [currentProfile]);
  const visibleProfileOptions = useMemo(() => mergeProfileOptions(profileOptions, currentProfileOption, getActiveMcpAuthorizedProfiles(deployment)), [currentProfileOption, deployment, profileOptions]);

  const hydrateDeploymentAuthState = useCallback((nextDeployment) => {
    if (!nextDeployment) return;
    const basicAccess = asArray(readValue(nextDeployment, "BasicClientAccess", "basicClientAccess"))
      .find((access) => readValue(access, "IsDeleted", "isDeleted") !== true && readValue(access, "IsActive", "isActive") !== false)
      || asArray(readValue(nextDeployment, "BasicClientAccess", "basicClientAccess"))[0]
      || null;
    const authorizedProfileIds = getMcpAuthorizedProfileIds(nextDeployment);
    const currentProfileId = getProfileId(currentProfile);
    setForm((previous) => ({
      ...previous,
      ApiAuthenticationMode: String(getDeploymentAuthMode(nextDeployment)),
      APIBasicAuthScope: readValue(nextDeployment, "APIBasicAuthScope", "apiBasicAuthScope") || "",
      AzureClientKey: readValue(basicAccess, "AzureClientKey", "azureClientKey") || "",
      FriendlyClientName: readValue(basicAccess, "FriendlyClientName", "friendlyClientName", "ServiceProfileName", "serviceProfileName") || "",
      McpOAuthScopes: readValue(nextDeployment, "McpOAuthScopes", "mcpOAuthScopes") || "",
      McpExecutionIdentityMode: String(getMcpIdentityMode(nextDeployment)),
      McpAuthorizationMode: String(readValue(nextDeployment, "McpAuthorizationMode", "mcpAuthorizationMode") || MCP_AUTH_MODE_SELECTED_PROFILES),
      McpAuthorizedProfileIds: authorizedProfileIds.length ? authorizedProfileIds : (currentProfileId ? [currentProfileId] : [])
    }));
    setTestCredentials(EMPTY_TEST_CREDENTIALS);
    const currentValues = valuesRef.current || {};
    const nextCredentialTestStatus = {
      pat: readValue(nextDeployment, "PatAuthenticationTestStatus", "patAuthenticationTestStatus")
        || readValue(currentValues, "PatAuthenticationTestStatus", "patAuthenticationTestStatus")
        || CREDENTIAL_TEST_STATUS_PENDING,
      basic: readValue(nextDeployment, "BasicAuthenticationTestStatus", "basicAuthenticationTestStatus")
        || readValue(currentValues, "BasicAuthenticationTestStatus", "basicAuthenticationTestStatus")
        || CREDENTIAL_TEST_STATUS_PENDING
    };
    credentialTestStatusRef.current = nextCredentialTestStatus;
    setCredentialTestStatus(nextCredentialTestStatus);
  }, [currentProfile]);

  useEffect(() => {
    let cancelled = false;
    const loadProfileOptions = async () => {
      if (!mcpEnabled) {
        setProfileOptions([]);
        return;
      }

      const endpointClientId = readValue(endpointRef.current, "ClientId", "clientId")
        || readValue(currentProfile, "ClientId", "clientId")
        || readValue(coreContext, "ClientId", "clientId");
      const manager = profileMgrRef.current;
      setProfilesLoading(true);
      try {
        let profileItems = asArray(readValue(manager, "items", "Items", "data", "Data"));
        if (profileItems.length === 0 && manager?.Query) {
          const query = endpointClientId
            ? {
                model: "x => x.IsDeleted == false && x.IsActive == true && x.ClientId == clientId",
                params: { clientId: endpointClientId },
                clientId: endpointClientId
              }
            : {
                model: "x => x.IsDeleted == false && x.IsActive == true",
                params: {}
              };
          const result = await manager.Query(query);
          profileItems = getOperationItems(result);
        }

        const options = profileItems
          .filter((profile) => readValue(profile, "IsDeleted", "isDeleted") !== true)
          .filter((profile) => readValue(profile, "IsActive", "isActive") !== false)
          .filter((profile) => !isBasicClientProfile(profile))
          .filter((profile) => {
            if (!endpointClientId) return true;
            const profileClientId = readValue(profile, "ClientId", "clientId");
            return !profileClientId || String(profileClientId) === String(endpointClientId);
          })
          .map(buildProfileOption)
          .filter(Boolean);

        if (!cancelled) {
          setProfileOptions(mergeProfileOptions(options, currentProfileOption, getActiveMcpAuthorizedProfiles(deploymentRef.current)));
        }
      } catch (ex) {
        if (!cancelled) {
          setProfileOptions(mergeProfileOptions(currentProfileOption, getActiveMcpAuthorizedProfiles(deploymentRef.current)));
        }
      } finally {
        if (!cancelled) setProfilesLoading(false);
      }
    };

    void loadProfileOptions();
    return () => {
      cancelled = true;
    };
  }, [coreContext, currentProfile, currentProfileOption, mcpEnabled]);

  const updateAuthStatus = useCallback((nextStatus) => {
    setAuthStatus(nextStatus);
    onChange?.("AuthenticationSetupStatus", nextStatus);
  }, [onChange]);

  const saveCredentialTestStatusToDeployment = useCallback(async (nextStatus = credentialTestStatusRef.current, testKeys = ["pat", "basic"], options = {}) => {
    const activeMgr = endpointMgrRef.current;
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    if (!activeMgr || !currentEndpoint || !currentDeployment) return;

    const keysToSave = Array.isArray(testKeys) && testKeys.length ? testKeys : ["pat", "basic"];
    const deploymentId = getDeploymentRecordId(currentDeployment);
    const rows = getDestinationDeploymentRows(currentEndpoint, productResourceDefaults).map((row) => {
      const normalized = normalizeDeploymentRow(row, currentEndpoint, productResourceDefaults);
      if (String(getDeploymentRecordId(normalized)) !== String(deploymentId)) return normalized;
      const nextRow = { ...normalized };
      if (keysToSave.includes("pat")) nextRow.PatAuthenticationTestStatus = nextStatus.pat;
      if (keysToSave.includes("basic")) nextRow.BasicAuthenticationTestStatus = nextStatus.basic;
      return nextRow;
    });
    const nextEndpoint = buildDestinationDeploymentSavePayload(currentEndpoint, productResourceDefaults, rows);
    const savedEndpoint = await saveDestination({
      manager: activeMgr,
      destination: nextEndpoint,
      coreState: coreStateRef.current,
      fallbackMessage: "Credential test status could not be saved."
    });
    const savedRows = getDestinationDeploymentRows(savedEndpoint, productResourceDefaults).map((row) =>
      normalizeDeploymentRow(row, savedEndpoint, productResourceDefaults)
    );
    const savedDeployment = savedRows.find((row) => String(getDeploymentRecordId(row)) === String(deploymentId))
      || getActiveDeployment(savedRows);
    endpointRef.current = savedEndpoint;
    deploymentRef.current = savedDeployment;
    setEndpoint(savedEndpoint);
    setDeployment(savedDeployment);
    setSelectedDeploymentId(getDeploymentRecordId(savedDeployment) || "");
    if (shouldNotifySaved(options)) onSaved?.(savedEndpoint, savedDeployment);
  }, [onSaved, productResourceDefaults, shouldNotifySaved]);

  const updateCredentialTestStatus = useCallback((testKey, nextStatus, options = {}) => {
    let resolvedStatus = credentialTestStatusRef.current;
    setCredentialTestStatus((previous) => ({ ...previous, [testKey]: nextStatus }));
    resolvedStatus = { ...credentialTestStatusRef.current, [testKey]: nextStatus };
    credentialTestStatusRef.current = resolvedStatus;
    if (options?.notifyChange !== false) {
      if (testKey === "pat") onChange?.("PatAuthenticationTestStatus", nextStatus);
      if (testKey === "basic") onChange?.("BasicAuthenticationTestStatus", nextStatus);
    }
    return resolvedStatus;
  }, [onChange]);

  const resetCredentialTestStatus = useCallback((testKeys = ["pat", "basic"], options = {}) => {
    let nextStatus = credentialTestStatusRef.current;
    testKeys.forEach((testKey) => {
      nextStatus = updateCredentialTestStatus(testKey, CREDENTIAL_TEST_STATUS_PENDING, options);
    });
    updateAuthStatus(AUTH_STATUS_CONFIGURED);
    saveCredentialTestStatusToDeployment(nextStatus, testKeys, options).catch((ex) => {
      setLocalError(getErrorSummary(ex, "Credential test status could not be saved."));
    });
  }, [saveCredentialTestStatusToDeployment, updateAuthStatus, updateCredentialTestStatus]);

  const updateForm = useCallback((fieldName, value) => {
    setForm((previous) => ({ ...previous, [fieldName]: value }));
    onChange?.(fieldName, value);

    if (["APIBasicAuthScope", "McpOAuthScopes", "McpExecutionIdentityMode", "McpAuthorizationMode", "McpAuthorizedProfileIds"].includes(fieldName)) {
      resetCredentialTestStatus(["pat", "basic"]);
    }
  }, [onChange, resetCredentialTestStatus]);

  const updateTestCredentialField = useCallback((fieldName, value) => {
    setTestCredentials((previous) => ({ ...previous, [fieldName]: value }));
  }, []);

  const selectAuthTab = useCallback((nextTab) => {
    const normalizedTab = normalizeAuthTab(nextTab);
    setActiveAuthTab(normalizedTab);
    writeStoredAuthTab(authTabStorageKey, normalizedTab);
  }, [authTabStorageKey]);

  useEffect(() => {
    const storedTab = readStoredAuthTab(authTabStorageKey);
    if (!storedTab || storedTab === activeAuthTab) return;
    setActiveAuthTab(storedTab);
  }, [activeAuthTab, authTabStorageKey]);

  const selectDeployment = useCallback((nextValue) => {
    const nextDeploymentId = fieldValue(nextValue);
    const nextDeployment = deploymentRows.find((row, index) =>
      String(getDeploymentRecordId(row) || index) === String(nextDeploymentId)
    );
    if (!nextDeployment) return;
    setSelectedDeploymentId(nextDeploymentId);
    deploymentRef.current = nextDeployment;
    setDeployment(nextDeployment);
    hydrateDeploymentAuthState(nextDeployment);
    setLocalError("");
  }, [deploymentRows, hydrateDeploymentAuthState]);

  const getAuthStatusForSave = useCallback((currentStatus = authStatus) => {
    if (!currentStatus || currentStatus === "Not configured") return AUTH_STATUS_CONFIGURED;
    return currentStatus;
  }, [authStatus]);

  const closeResultDialog = useCallback(() => {
    setResultDialog({
      open: false,
      title: "",
      content: "",
      html: "",
      copyValue: "",
      copyLabel: "",
      copyStatus: "",
      downloadAction: null,
      downloadStatus: ""
    });
  }, []);

  const copyResultDialogValue = useCallback(async () => {
    const value = String(resultDialog.copyValue || "");
    if (!value) return;

    try {
      await copyTextToClipboard(value);
      setResultDialog((previous) => ({ ...previous, copyStatus: "PAT secret copied." }));
    } catch (ex) {
      setResultDialog((previous) => ({
        ...previous,
        copyStatus: getErrorSummary(ex, "Clipboard copy failed. Select the PAT secret in the dialog and copy it manually.")
      }));
    }
  }, [resultDialog.copyValue]);

  const showBasicClientAccessHelp = useCallback(() => {
    const currentEndpoint = endpointRef.current || endpoint;
    const currentDeployment = deploymentRef.current || deployment;
    const downloadState = getBasicClientAccessDownloadState({ endpoint: currentEndpoint, endpointId, deployment: currentDeployment });
    setResultDialog({
      open: true,
      title: "Create Azure Basic Client Access",
      content: "",
      html: buildBasicClientAccessHelpHtml({
        endpoint: currentEndpoint,
        deployment: currentDeployment,
        profile: currentProfile,
        form,
        wizard,
        coreContext
      }),
      downloadAction: {
        operationName: productResourceDefaults.downloadBasicClientAccessScriptOperationName || "DownloadAPIEndpointBasicClientAccessScript",
        disabled: downloadState.disabled,
        disabledReason: downloadState.disabledReason
      },
      downloadStatus: downloadState.disabledReason
    });
  }, [coreContext, currentProfile, deployment, endpoint, endpointId, form, productResourceDefaults.downloadBasicClientAccessScriptOperationName, wizard]);

  useEffect(() => {
    if (!resultDialog.open || resultDialog.title !== "Create Azure Basic Client Access") return;
    const currentEndpoint = endpointRef.current || endpoint;
    const currentDeployment = deploymentRef.current || deployment;
    setResultDialog((previous) => ({
      ...previous,
      html: buildBasicClientAccessHelpHtml({
        endpoint: currentEndpoint,
        deployment: currentDeployment,
        profile: currentProfile,
        form,
        wizard,
        coreContext
      })
    }));
  }, [coreContext, currentProfile, deployment, endpoint, form, resultDialog.open, resultDialog.title, wizard]);

  const setBasicAccessItemData = useCallback((nextData = {}) => {
    const nextAzureClientKey = readValue(nextData, "AzureClientKey", "azureClientKey") ?? form.AzureClientKey ?? "";
    const nextFriendlyClientName = readValue(nextData, "FriendlyClientName", "friendlyClientName") ?? form.FriendlyClientName ?? "";
    const nextBasicAuthScope = readValue(nextData, "APIBasicAuthScope", "apiBasicAuthScope") ?? form.APIBasicAuthScope ?? "";
    setForm((previous) => ({
      ...previous,
      AzureClientKey: nextAzureClientKey,
      FriendlyClientName: nextFriendlyClientName,
      APIBasicAuthScope: nextBasicAuthScope
    }));
    onChange?.("AzureClientKey", nextAzureClientKey);
    onChange?.("FriendlyClientName", nextFriendlyClientName);
    onChange?.("APIBasicAuthScope", nextBasicAuthScope);
  }, [form.APIBasicAuthScope, form.AzureClientKey, form.FriendlyClientName, onChange]);

  useEffect(() => {
    let mounted = true;
    const activeMgr = endpointMgrRef.current;
    const loadKey = `${managerKey}:${endpointId || "none"}:${fixedDeploymentId || "active"}`;
    if (!endpointId) {
      setEndpoint(null);
      setDeployment(null);
      setLoading(false);
      return undefined;
    }
    if (!activeMgr) {
      setLoading(false);
      setLocalError(`${endpointLabel} service is not available.`);
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        if (hasLoadedManagerMetadata(activeMgr) || isCoreProfileMetadataReady(coreContext)) {
          await loadDestinationManagerMetadata({ manager: activeMgr, coreContext });
        }
        const loadedEndpoint = await loadDestinationById({
          manager: activeMgr,
          destinationId: endpointId,
          coreState: coreStateRef.current,
          fallbackMessage: `${endpointLabel} could not be loaded.`
        });
        if (!mounted) return;
        const rows = getDestinationDeploymentRows(loadedEndpoint, productResourceDefaults).map((row) =>
          normalizeDeploymentRow(row, loadedEndpoint, productResourceDefaults)
        );
        const activeDeployment = fixedDeploymentId
          ? rows.find((row) => deploymentMatchesId(row, fixedDeploymentId)) || null
          : getActiveDeployment(rows);
        setEndpoint(loadedEndpoint);
        setDeployment(activeDeployment);
        setSelectedDeploymentId(fixedDeploymentId || getDeploymentRecordId(activeDeployment) || "");
        if (activeDeployment) {
          hydrateDeploymentAuthState(activeDeployment);
        } else if (fixedDeploymentId) {
          setLocalError("The selected endpoint deployment is not available for authentication configuration.");
        }
      } catch (ex) {
        if (mounted) setLocalError(getErrorSummary(ex, `${endpointLabel} could not be loaded.`));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [coreContext, endpointId, endpointLabel, fixedDeploymentId, hydrateDeploymentAuthState, managerKey, productResourceDefaults]);

  const tokenSummary = useMemo(() => {
    const tokens = asArray(readValue(deployment, "APITokens", "apiTokens"));
    const activeTokens = tokens.filter(isActiveToken);
    const inactiveTokens = tokens.filter((token) => !isActiveToken(token));
    const displayTokens = sortTokensForDisplay(tokens);
    const latestCreated = tokens
      .map((token) => readValue(token, "CreatedDate", "createdDate", "CreateDate", "createDate"))
      .filter(Boolean)
      .sort()
      .at(-1);
    return { tokens, displayTokens, activeTokens, inactiveTokens, latestCreated };
  }, [deployment]);

  const applyDeploymentToEndpointState = useCallback((deploymentInput, endpointInput = endpointRef.current, options = {}) => {
    if (!deploymentInput) return { nextEndpoint: endpointInput || null, nextDeployment: null };
    const notifySaved = options?.notify !== false;

    const currentEndpoint = endpointInput || endpointRef.current;
    const nextDeployment = normalizeDeploymentRow(deploymentInput, currentEndpoint, productResourceDefaults);

    if (!currentEndpoint) {
      deploymentRef.current = nextDeployment;
      setDeployment(nextDeployment);
      return { nextEndpoint: null, nextDeployment };
    }

    const deploymentId = getDeploymentRecordId(nextDeployment);
    let matched = false;
    const rows = getDestinationDeploymentRows(currentEndpoint, productResourceDefaults)
      .map((row) => normalizeDeploymentRow(row, currentEndpoint, productResourceDefaults))
      .map((row) => {
        if (String(getDeploymentRecordId(row)) !== String(deploymentId)) return row;
        matched = true;
        return nextDeployment;
      });
    if (!matched) rows.push(nextDeployment);

    const nextEndpoint = buildDestinationDeploymentSavePayload(currentEndpoint, productResourceDefaults, rows);
    endpointRef.current = nextEndpoint;
    deploymentRef.current = nextDeployment;
    setEndpoint(nextEndpoint);
    setDeployment(nextDeployment);
    setSelectedDeploymentId(getDeploymentRecordId(nextDeployment) || "");
    if (notifySaved && shouldNotifySaved(options)) onSaved?.(nextEndpoint, nextDeployment);
    return { nextEndpoint, nextDeployment };
  }, [onSaved, productResourceDefaults, shouldNotifySaved]);

  const saveAuthConfiguration = useCallback(async (nextStatus = AUTH_STATUS_CONFIGURED, formOverrides = {}, options = {}) => {
    const activeMgr = endpointMgrRef.current;
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    if (!activeMgr) throw new Error(`${endpointLabel} service is not available.`);
    if (!currentEndpoint) throw new Error(`${endpointLabel} could not be loaded.`);
    if (!currentDeployment) throw new Error("Endpoint deployment could not be loaded.");

    const effectiveForm = { ...form, ...formOverrides };
    const effectiveBasicAuthScope = isBasicAuthMode(effectiveForm.ApiAuthenticationMode)
      ? resolveEffectiveBasicAuthScope(effectiveForm.APIBasicAuthScope, endpointAuthContext.recommendedBasicAuthScope)
      : (effectiveForm.APIBasicAuthScope || "");
    const effectiveMcpAuthorizationMode = Number(effectiveForm.McpAuthorizationMode) || MCP_AUTH_MODE_SELECTED_PROFILES;
    const effectiveMcpAuthorizedProfileIds = normalizeProfileIds(effectiveForm.McpAuthorizedProfileIds);
    if (mcpEnabled &&
      effectiveMcpAuthorizationMode === MCP_AUTH_MODE_SELECTED_PROFILES &&
      effectiveMcpAuthorizedProfileIds.length === 0) {
      throw new Error("Select at least one authorized profile for MCP access.");
    }
    const deploymentId = getDeploymentRecordId(currentDeployment);
    const endpointClientId = readValue(currentEndpoint, "ClientId", "clientId")
      || readValue(currentProfile, "ClientId", "clientId")
      || "";
    const rows = getDestinationDeploymentRows(currentEndpoint, productResourceDefaults).map((row) => {
      const normalized = normalizeDeploymentRow(row, currentEndpoint, productResourceDefaults);
      if (String(getDeploymentRecordId(normalized)) !== String(deploymentId)) return normalized;
      return {
        ...normalized,
        ApiAuthenticationMode: Number(effectiveForm.ApiAuthenticationMode) || AUTH_MODE_OAUTH_TOKEN,
        APIBasicAuthScope: effectiveBasicAuthScope,
        AuthenticationSetupStatus: nextStatus,
        PatAuthenticationTestStatus: credentialTestStatusRef.current.pat,
        BasicAuthenticationTestStatus: credentialTestStatusRef.current.basic,
        McpOAuthScopes: effectiveForm.McpOAuthScopes || "",
        McpExecutionIdentityMode: getMcpIdentityMode({ ...normalized, McpExecutionIdentityMode: effectiveForm.McpExecutionIdentityMode }),
        McpAuthorizationMode: effectiveMcpAuthorizationMode,
        McpAuthorizedProfiles: buildMcpAuthorizedProfileRows({
          deployment: normalized,
          selectedProfileIds: effectiveMcpAuthorizationMode === MCP_AUTH_MODE_SELECTED_PROFILES ? effectiveMcpAuthorizedProfileIds : [],
          profileOptions: visibleProfileOptions,
          endpointClientId
        })
      };
    });
    const nextEndpoint = buildDestinationDeploymentSavePayload(currentEndpoint, productResourceDefaults, rows);
    const savedEndpoint = await saveDestination({
      manager: activeMgr,
      destination: nextEndpoint,
      coreState: coreStateRef.current,
      fallbackMessage: "Authentication setup could not be saved."
    });
    const savedRows = getDestinationDeploymentRows(savedEndpoint, productResourceDefaults).map((row) =>
      normalizeDeploymentRow(row, savedEndpoint, productResourceDefaults)
    );
    const savedDeployment = savedRows.find((row) => String(getDeploymentRecordId(row)) === String(deploymentId))
      || getActiveDeployment(savedRows);
    endpointRef.current = savedEndpoint;
    deploymentRef.current = savedDeployment;
    setEndpoint(savedEndpoint);
    setDeployment(savedDeployment);
    setSelectedDeploymentId(getDeploymentRecordId(savedDeployment) || "");
    setForm((previous) => ({
      ...previous,
      APIBasicAuthScope: effectiveBasicAuthScope,
      McpAuthorizationMode: String(readValue(savedDeployment, "McpAuthorizationMode", "mcpAuthorizationMode") || effectiveMcpAuthorizationMode),
      McpAuthorizedProfileIds: getMcpAuthorizedProfileIds(savedDeployment)
    }));
    onChange?.("APIBasicAuthScope", effectiveBasicAuthScope);
    onChange?.("McpAuthorizationMode", String(readValue(savedDeployment, "McpAuthorizationMode", "mcpAuthorizationMode") || effectiveMcpAuthorizationMode));
    onChange?.("McpAuthorizedProfileIds", getMcpAuthorizedProfileIds(savedDeployment));
    updateAuthStatus(nextStatus);
    if (shouldNotifySaved(options)) onSaved?.(savedEndpoint, savedDeployment);
    return { savedEndpoint, savedDeployment, effectiveBasicAuthScope, effectiveForm };
  }, [currentProfile, endpointAuthContext.recommendedBasicAuthScope, endpointLabel, form, mcpEnabled, onChange, onSaved, productResourceDefaults, shouldNotifySaved, updateAuthStatus, visibleProfileOptions]);

  const prepareAuthStepValues = useCallback(async (currentValues = values, nextStatus = AUTH_STATUS_CONFIGURED) => {
    try {
      const {
        savedEndpoint,
        savedDeployment,
        effectiveBasicAuthScope,
        effectiveForm
      } = await saveAuthConfiguration(nextStatus);
      const savedRows = getDestinationDeploymentRows(savedEndpoint, productResourceDefaults).map((row) =>
        normalizeDeploymentRow(row, savedEndpoint, productResourceDefaults)
      );
      const savedMode = readValue(savedDeployment, "ApiAuthenticationMode", "apiAuthenticationMode")
        || effectiveForm?.ApiAuthenticationMode
        || form.ApiAuthenticationMode
        || AUTH_MODE_OAUTH_TOKEN;
      const savedBasicScope = effectiveBasicAuthScope
        || readValue(savedDeployment, "APIBasicAuthScope", "apiBasicAuthScope")
        || "";
      const savedMcpAuthorizedProfileIds = getMcpAuthorizedProfileIds(savedDeployment);

      setLocalError("");
      return {
        ...buildDeploymentStepValues({
          currentValues,
          destination: savedEndpoint,
          deployments: savedRows
        }),
        ApiAuthenticationMode: String(savedMode),
        APIBasicAuthScope: savedBasicScope,
        AuthenticationSetupStatus: nextStatus,
        PatAuthenticationTestStatus: credentialTestStatus.pat,
        BasicAuthenticationTestStatus: credentialTestStatus.basic,
        McpAuthorizationMode: String(readValue(savedDeployment, "McpAuthorizationMode", "mcpAuthorizationMode") || effectiveForm?.McpAuthorizationMode || MCP_AUTH_MODE_SELECTED_PROFILES),
        McpAuthorizedProfileIds: savedMcpAuthorizedProfileIds,
        McpAuthorizedProfileCount: savedMcpAuthorizedProfileIds.length
      };
    } catch (ex) {
      const message = getErrorSummary(ex, "Authentication setup could not be saved.");
      setLocalError(message);
      throw buildWizardValidationError(message, { suppressSummary: true });
    }
  }, [credentialTestStatus.basic, credentialTestStatus.pat, form.ApiAuthenticationMode, productResourceDefaults, saveAuthConfiguration, values]);

  const confirmAuthContinue = useCallback(() => {
    if (Number(form.ApiAuthenticationMode) !== AUTH_MODE_ANONYMOUS) return true;
    if (anonymousContinueApprovedRef.current) {
      anonymousContinueApprovedRef.current = false;
      return true;
    }
    setAnonymousDialogOpen(true);
    return false;
  }, [form.ApiAuthenticationMode]);

  const saveAndContinue = useCallback(async ({ skipAnonymousWarning = false } = {}) => {
    if (Number(form.ApiAuthenticationMode) === AUTH_MODE_ANONYMOUS && !skipAnonymousWarning) {
      setAnonymousDialogOpen(true);
      return;
    }
    setBusy(true);
    setLocalError("");
    try {
      await saveAuthConfiguration(getAuthStatusForSave());
      if (skipAnonymousWarning) anonymousContinueApprovedRef.current = true;
      if (!managementMode) onContinue?.();
    } catch (ex) {
      setLocalError(getErrorSummary(ex, "Authentication setup could not be saved."));
    } finally {
      setBusy(false);
    }
  }, [form.ApiAuthenticationMode, getAuthStatusForSave, managementMode, onContinue, saveAuthConfiguration]);

  const skipAuthentication = useCallback(() => {
    updateAuthStatus(AUTH_STATUS_SKIPPED);
    onContinue?.();
  }, [onContinue, updateAuthStatus]);

  const runAuthOperation = useCallback(async ({ operationName, item, queryParams, fallbackMessage }) => {
    const activeMgr = endpointMgrRef.current;
    if (!activeMgr?.RunOperation) throw new Error(`${endpointLabel} operation service is not available.`);
    const result = await activeMgr.RunOperation({
      operationName,
      item,
      queryParams,
      isItem: true,
      isWarning: true,
      deferValueUICommands: true
    });
    requireSuccessfulServiceResponse(result, fallbackMessage, coreStateRef.current);
    return result;
  }, [endpointLabel]);

  const downloadBasicClientAccessScript = useCallback(async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    const downloadState = getBasicClientAccessDownloadState({
      endpoint: currentEndpoint,
      endpointId,
      deployment: currentDeployment
    });

    if (downloadState.disabled) {
      setResultDialog((previous) => ({
        ...previous,
        downloadStatus: downloadState.disabledReason,
        downloadAction: previous.downloadAction
          ? { ...previous.downloadAction, disabled: true, disabledReason: downloadState.disabledReason }
          : previous.downloadAction
      }));
      return;
    }

    const operationName = productResourceDefaults.downloadBasicClientAccessScriptOperationName || "DownloadAPIEndpointBasicClientAccessScript";
    setBusy(true);
    setLocalError("");
    setResultDialog((previous) => ({
      ...previous,
      downloadStatus: "Preparing script..."
    }));

    try {
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: currentEndpoint,
        queryParams: {
          id: downloadState.deploymentId,
          deploymentId: downloadState.deploymentId,
          apiId: downloadState.apiId,
          endpointId: downloadState.apiId,
          schemaApiId: downloadState.apiId,
          basicAuthScope: resolveEffectiveBasicAuthScope(form.APIBasicAuthScope, endpointAuthContext.recommendedBasicAuthScope)
        }
      });
      const result = await runAuthOperation({
        operationName,
        queryParams,
        fallbackMessage: "Azure setup script could not be prepared."
      });
      const returnedDeployment = getOperationItem(result);
      if (returnedDeployment) applyDeploymentToEndpointState(returnedDeployment, currentEndpoint, { notify: false });
      const resultValue = getResultDisplayValue(result);
      const html = typeof resultValue === "string" ? sanitizeHtmlFragment(resultValue) : "";
      setResultDialog((previous) => ({
        ...previous,
        open: true,
        title: "Azure Setup Script",
        html,
        content: html ? "" : safeJson(resultValue),
        copyValue: "",
        copyLabel: "",
        copyStatus: "",
        downloadAction: {
          operationName,
          disabled: false,
          disabledReason: ""
        },
        downloadStatus: "Script is ready."
      }));
    } catch (ex) {
      setResultDialog((previous) => ({
        ...previous,
        downloadStatus: getErrorSummary(ex, "Azure setup script could not be prepared.")
      }));
    } finally {
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, endpointAuthContext.recommendedBasicAuthScope, endpointId, form.APIBasicAuthScope, productResourceDefaults.downloadBasicClientAccessScriptOperationName, runAuthOperation, wizard]);

  const downloadMcpOAuthSetupScript = useCallback(async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    const downloadState = getMcpOAuthSetupDownloadState({
      endpoint: currentEndpoint,
      endpointId,
      deployment: currentDeployment
    });

    if (downloadState.disabled) {
      setResultDialog((previous) => ({
        ...previous,
        downloadStatus: downloadState.disabledReason,
        downloadAction: previous.downloadAction
          ? { ...previous.downloadAction, disabled: true, disabledReason: downloadState.disabledReason }
          : previous.downloadAction
      }));
      return;
    }

    const operationName = productResourceDefaults.downloadMcpOAuthSetupScriptOperationName || "DownloadAPIEndpointMcpOAuthSetupScript";
    setBusy(true);
    setLocalError("");
    setResultDialog((previous) => ({
      ...previous,
      downloadStatus: "Preparing MCP OAuth setup script..."
    }));

    try {
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: currentEndpoint,
        queryParams: {
          id: downloadState.deploymentId,
          deploymentId: downloadState.deploymentId,
          apiId: downloadState.apiId,
          endpointId: downloadState.apiId,
          schemaApiId: downloadState.apiId
        }
      });
      const result = await runAuthOperation({
        operationName,
        queryParams,
        fallbackMessage: "MCP OAuth setup script could not be prepared."
      });
      const returnedDeployment = getOperationItem(result);
      if (returnedDeployment) applyDeploymentToEndpointState(returnedDeployment, currentEndpoint, { notify: false });
      const resultValue = getResultDisplayValue(result);
      const html = typeof resultValue === "string" ? sanitizeHtmlFragment(resultValue) : "";
      setResultDialog((previous) => ({
        ...previous,
        open: true,
        title: "MCP OAuth Setup Script",
        html,
        content: html ? "" : safeJson(resultValue),
        copyValue: "",
        copyLabel: "",
        copyStatus: "",
        downloadAction: {
          kind: "mcpOAuth",
          operationName,
          disabled: false,
          disabledReason: ""
        },
        downloadStatus: "Script is ready."
      }));
    } catch (ex) {
      setResultDialog((previous) => ({
        ...previous,
        downloadStatus: getErrorSummary(ex, "MCP OAuth setup script could not be prepared.")
      }));
    } finally {
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, endpointId, productResourceDefaults.downloadMcpOAuthSetupScriptOperationName, runAuthOperation, wizard]);

  const runHostedApiAuthenticationTest = useCallback(async ({ authType: requestedAuthType, credential, title, testStatusKey, event } = {}) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    selectAuthTab(AUTH_TAB_TEST);
    setBusy(true);
    setLocalError("");
    try {
      const authorization = buildAuthorizationHeaderForTest(requestedAuthType, credential);
      if (!authorization) throw new Error("Authentication credential is required.");

      const { savedEndpoint, savedDeployment } = await saveAuthConfiguration(AUTH_STATUS_CONFIGURED, {}, { notify: requestedAuthType !== "McpOAuth" });
      const authContext = resolveEndpointAuthContext({
        coreContext,
        profile: currentProfile,
        endpoint: savedEndpoint,
        deployment: savedDeployment,
        wizard
      });
      if (authContext.errorMessage) throw new Error(authContext.errorMessage);

      const authTestUrl = buildEndpointAuthTestUrl(authContext);
      if (!authTestUrl) throw new Error("Hosted API base URL is not configured.");
      if (typeof fetch !== "function") throw new Error("Hosted API auth test cannot run in this browser.");

      const response = await fetch(authTestUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: authorization
        }
      });
      const resultValue = await readEndpointAuthTestResponse(response);
      const nextCredentialTestStatus = testStatusKey
        ? updateCredentialTestStatus(testStatusKey, CREDENTIAL_TEST_STATUS_SUCCESS)
        : credentialTestStatusRef.current;
      updateAuthStatus(AUTH_STATUS_TEST_PASSED);
      await saveCredentialTestStatusToDeployment(nextCredentialTestStatus, testStatusKey ? [testStatusKey] : ["pat", "basic"]);
      setResultDialog({
        open: true,
        title: title || "Authentication Test Result",
        html: buildAuthTestResultHtml(resultValue),
        content: safeJson(resultValue)
      });
    } catch (ex) {
      const nextCredentialTestStatus = testStatusKey
        ? updateCredentialTestStatus(testStatusKey, CREDENTIAL_TEST_STATUS_FAILED)
        : credentialTestStatusRef.current;
      updateAuthStatus(AUTH_STATUS_TEST_FAILED);
      await saveCredentialTestStatusToDeployment(nextCredentialTestStatus, testStatusKey ? [testStatusKey] : ["pat", "basic"]);
      setLocalError(getErrorSummary(ex, "Authentication test failed."));
    } finally {
      setBusy(false);
    }
  }, [coreContext, currentProfile, saveAuthConfiguration, saveCredentialTestStatusToDeployment, selectAuthTab, updateAuthStatus, updateCredentialTestStatus, wizard]);

  const runAuthenticationTest = useCallback(async ({ authType: requestedAuthType, credential, title, event } = {}) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const suppressSavedNotification = requestedAuthType === "McpOAuth";
    if (suppressSavedNotification) suppressSavedNotificationRef.current = true;
    selectAuthTab(AUTH_TAB_TEST);
    setBusy(true);
    setLocalError("");
    try {
      const { savedEndpoint, savedDeployment } = await saveAuthConfiguration(AUTH_STATUS_CONFIGURED);
      const deploymentId = getDeploymentRecordId(savedDeployment);
      const apiId = endpointId || readValue(savedEndpoint, "_id", "id", "UniqueId", "uniqueId");
      const requestedCredential = credential ?? form.TestCredential ?? "";
      const effectiveCredential = requestedAuthType === "McpOAuth" && !String(requestedCredential || "").trim()
        ? getCurrentLoggedInAccessToken()
        : requestedCredential;
      const authType = requestedAuthType || deriveAuthTestType(
        savedDeployment,
        Number(readValue(savedDeployment, "ApiAuthenticationMode", "apiAuthenticationMode")) || AUTH_MODE_OAUTH_TOKEN,
        effectiveCredential
      );
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: savedEndpoint,
        queryParams: {
          id: deploymentId,
          deploymentId,
          apiId,
          endpointId: apiId,
          schemaApiId: apiId,
          authType,
          credential: effectiveCredential || "",
          origin: getCurrentBrowserOrigin()
        }
      });
      const result = await runAuthOperation({
        operationName: productResourceDefaults.checkAuthenticationOperationName || "CheckAPIEndpointAuthentication",
        item: savedDeployment,
        queryParams,
        fallbackMessage: "Authentication test failed."
      });
      const returnedDeployment = getOperationItem(result);
      if (returnedDeployment) applyDeploymentToEndpointState(returnedDeployment, savedEndpoint, { notify: requestedAuthType !== "McpOAuth" });
      const resultValue = getResultDisplayValue(result);
      const statusSave = await saveAuthConfiguration(AUTH_STATUS_TEST_PASSED, {}, { notify: requestedAuthType !== "McpOAuth" });
      applyDeploymentToEndpointState(statusSave.savedDeployment, statusSave.savedEndpoint, { notify: requestedAuthType !== "McpOAuth" });
      setResultDialog({
        open: true,
        title: title || "Authentication Test Result",
        html: buildAuthTestResultHtml(resultValue),
        content: safeJson(resultValue)
      });
    } catch (ex) {
      updateAuthStatus(AUTH_STATUS_TEST_FAILED);
      setLocalError(getErrorSummary(ex, "Authentication test failed."));
    } finally {
      if (suppressSavedNotification) suppressSavedNotificationRef.current = false;
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, endpointId, form.TestCredential, productResourceDefaults.checkAuthenticationOperationName, runAuthOperation, saveAuthConfiguration, selectAuthTab, updateAuthStatus, wizard]);

  const runMcpAuthenticationTest = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    return runAuthenticationTest({
      authType: "McpOAuth",
      credential: testCredentials.McpTestCredential,
      title: "MCP OAuth Test Result",
      event
    });
  }, [runAuthenticationTest, testCredentials.McpTestCredential]);

  const saveBasicClientAccess = useCallback(async (basicAccessItem = {}) => {
    const basicAccessValues = {
      AzureClientKey: readValue(basicAccessItem, "AzureClientKey", "azureClientKey") ?? form.AzureClientKey ?? "",
      FriendlyClientName: readValue(basicAccessItem, "FriendlyClientName", "friendlyClientName") ?? form.FriendlyClientName ?? "",
      APIBasicAuthScope: readValue(basicAccessItem, "APIBasicAuthScope", "apiBasicAuthScope") ?? form.APIBasicAuthScope ?? ""
    };
    const scopeInputError = getBasicAuthScopeInputError(basicAccessValues.APIBasicAuthScope, endpointAuthContext.recommendedBasicAuthScope);
    if (scopeInputError) {
      setLocalError(scopeInputError);
      return;
    }
    setBasicAccessItemData(basicAccessValues);
    setBusy(true);
    setLocalError("");
    try {
      const { savedEndpoint, savedDeployment } = await saveAuthConfiguration(AUTH_STATUS_CONFIGURED, basicAccessValues);
      const deploymentId = getDeploymentRecordId(savedDeployment);
      const apiId = endpointId || readValue(savedEndpoint, "_id", "id", "UniqueId", "uniqueId");
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: savedEndpoint,
        queryParams: {
          id: deploymentId,
          deploymentId,
          apiId,
          endpointId: apiId,
          schemaApiId: apiId,
          azureClientKey: basicAccessValues.AzureClientKey,
          friendlyClientName: basicAccessValues.FriendlyClientName,
          basicAuthScope: basicAccessValues.APIBasicAuthScope
        }
      });
      const result = await runAuthOperation({
        operationName: productResourceDefaults.saveBasicClientAccessOperationName || "SaveAPIEndpointBasicClientAccess",
        item: savedDeployment,
        queryParams,
        fallbackMessage: "Basic client access could not be saved."
      });
      const savedAccess = getOperationItem(result);
      if (savedAccess) {
        const savedClientKey = String(basicAccessValues.AzureClientKey || "").trim().toLowerCase();
        const nextDeployment = {
          ...savedDeployment,
          APIBasicAuthScope: basicAccessValues.APIBasicAuthScope,
          BasicClientAccess: [
            savedAccess,
            ...asArray(readValue(savedDeployment, "BasicClientAccess", "basicClientAccess")).filter((access) =>
              readValue(access, "IsDeleted", "isDeleted") !== true &&
              String(readValue(access, "AzureClientKey", "azureClientKey") || "").trim().toLowerCase() !== savedClientKey)
          ]
        };
        applyDeploymentToEndpointState(nextDeployment, savedEndpoint);
      }
      setBasicAccessPanelOpen(false);
      resetCredentialTestStatus(["basic"]);
    } catch (ex) {
      setLocalError(getErrorSummary(ex, "Basic client access could not be saved."));
    } finally {
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, endpointAuthContext.recommendedBasicAuthScope, endpointId, form.APIBasicAuthScope, form.AzureClientKey, form.FriendlyClientName, productResourceDefaults.saveBasicClientAccessOperationName, resetCredentialTestStatus, runAuthOperation, saveAuthConfiguration, setBasicAccessItemData, wizard]);

  const createToken = useCallback(async () => {
    setBusy(true);
    setLocalError("");
    try {
      const { savedEndpoint, savedDeployment } = await saveAuthConfiguration(AUTH_STATUS_CONFIGURED);
      const deploymentId = getDeploymentRecordId(savedDeployment);
      const apiId = endpointId || readValue(savedEndpoint, "_id", "id", "UniqueId", "uniqueId");
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: savedEndpoint,
        queryParams: {
          id: deploymentId,
          deploymentId,
          apiId,
          endpointId: apiId,
          schemaApiId: apiId,
          tokenLabel: tokenForm.TokenLabel,
          expirationDuration: tokenForm.ExpirationDuration || "1m"
        }
      });
      const result = await runAuthOperation({
        operationName: productResourceDefaults.createTokenOperationName || "CreateAPIEndpointPersonalAccessToken",
        item: savedDeployment,
        queryParams,
        fallbackMessage: "PAT token could not be created."
      });
      const returnedDeployment = getOperationItem(result);
      if (returnedDeployment) applyDeploymentToEndpointState(returnedDeployment, savedEndpoint);
      resetCredentialTestStatus(["pat"]);
      const tokenResult = extractPatTokenResult(result);
      setTokenDialogOpen(false);
      setTokenForm({ TokenLabel: "", ExpirationDuration: "1m" });
      setResultDialog({
        open: true,
        title: "PAT Token Created",
        html: tokenResult.html || "",
        copyValue: tokenResult.tokenSecret || "",
        copyLabel: "Copy PAT Secret",
        content: tokenResult.html
          ? ""
          : safeJson({
            message: tokenResult.message,
            tokenSecret: tokenResult.tokenSecret || "Secret was not returned by the operation.",
            tokenLabel: tokenResult.tokenLabel || tokenForm.TokenLabel,
            permissionKey: tokenResult.permissionKey,
            expirationDate: tokenResult.expirationDate,
            tokenFingerprint: tokenResult.tokenFingerprint
          })
      });
      return { Success: true };
    } catch (ex) {
      const message = getErrorSummary(ex, "PAT token could not be created.");
      setLocalError(message);
      return {
        Success: false,
        Message: { DisplayMessage: message }
      };
    } finally {
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, endpointId, productResourceDefaults, resetCredentialTestStatus, runAuthOperation, saveAuthConfiguration, tokenForm.ExpirationDuration, tokenForm.TokenLabel, wizard]);

  const revokeToken = useCallback(async (token) => {
    const tokenId = getTokenId(token);
    if (!tokenId) return;
    setBusy(true);
    setLocalError("");
    try {
      const { savedEndpoint, savedDeployment } = await saveAuthConfiguration(AUTH_STATUS_CONFIGURED);
      const queryParams = buildResourceOperationQueryParams({
        wizard,
        item: savedEndpoint,
        queryParams: { id: tokenId, tokenId }
      });
      const result = await runAuthOperation({
        operationName: productResourceDefaults.revokeTokenOperationName || "RevokeAPIEndpointPersonalAccessToken",
        item: { ...token, UniqueId: tokenId },
        queryParams,
        fallbackMessage: "PAT token could not be revoked."
      });
      const operationItem = getOperationItem(result);
      const nextDeployment = {
        ...savedDeployment,
        APITokens: asArray(readValue(savedDeployment, "APITokens", "apiTokens")).map((existingToken) =>
          String(getTokenId(existingToken)) === String(tokenId)
            ? { ...existingToken, ...operationItem, IsActive: false, RevokedDate: readValue(operationItem, "RevokedDate", "revokedDate") || new Date().toISOString() }
            : existingToken
        )
      };
      applyDeploymentToEndpointState(nextDeployment, savedEndpoint);
      resetCredentialTestStatus(["pat"]);
    } catch (ex) {
      setLocalError(getErrorSummary(ex, "PAT token could not be revoked."));
    } finally {
      setBusy(false);
    }
  }, [applyDeploymentToEndpointState, productResourceDefaults, resetCredentialTestStatus, runAuthOperation, saveAuthConfiguration, wizard]);

  useEffect(() => {
    registerStepController?.({
      get isBusy() {
        return Boolean(busyRef.current);
      },
      prepareSave: (currentValues) => prepareAuthStepValues(currentValues, getAuthStatusForSave()),
      confirmContinue: confirmAuthContinue,
      prepareContinue: (currentValues) => prepareAuthStepValues(currentValues, getAuthStatusForSave())
    });
    return () => registerStepController?.(null);
  }, [confirmAuthContinue, getAuthStatusForSave, prepareAuthStepValues, registerStepController]);

  const authMode = Number(form.ApiAuthenticationMode) || AUTH_MODE_OAUTH_TOKEN;
  const savedAuthMode = Number(getDeploymentAuthMode(deployment)) || AUTH_MODE_OAUTH_TOKEN;
  const authModeChanged = authMode !== savedAuthMode;
  const isAnonymous = authMode === AUTH_MODE_ANONYMOUS;
  const isBasicMode = authMode === AUTH_MODE_BASIC || authMode === AUTH_MODE_OAUTH_AND_BASIC;
  const isBearerMode = authMode === AUTH_MODE_OAUTH_TOKEN || authMode === AUTH_MODE_OAUTH_AND_BASIC;
  const modeDetails = getAuthModeDetails(authMode);
  const authContext = resolveEndpointAuthContext({ coreContext, profile: currentProfile, endpoint, deployment, wizard });
  const authExampleLines = buildEndpointAuthExampleLines(authMode, authContext);
  const basicAuthExampleLines = authContext.errorMessage
    ? []
    : [`GET ${authContext.apiRouteExample}`, "Authorization: Basic base64(clientKey:clientSecret)"];
  const mcpAuthExampleLines = authContext.errorMessage
    ? []
    : [`GET|POST ${authContext.mcpRouteExample}`, "Authorization: Bearer {mcp-oauth-token}"];
  const mcpAccessMode = Number(form.McpAuthorizationMode) || MCP_AUTH_MODE_SELECTED_PROFILES;
  const mcpAuthorizedProfileIds = normalizeProfileIds(form.McpAuthorizedProfileIds);
  const mcpIdentityConfigured = Number(form.McpExecutionIdentityMode) > 0;
  const mcpAccessConfigured = mcpAccessMode === MCP_AUTH_MODE_ALL_AUTHENTICATED_USERS || mcpAuthorizedProfileIds.length > 0;
  const mcpAuthenticationConfigured = !mcpEnabled || (mcpIdentityConfigured && mcpAccessConfigured);
  const mcpAuthenticationTestPassed = mcpEnabled && authStatus === AUTH_STATUS_TEST_PASSED;
  const mcpTestStatus = authStatus === AUTH_STATUS_TEST_PASSED
    ? CREDENTIAL_TEST_STATUS_SUCCESS
    : authStatus === AUTH_STATUS_TEST_FAILED
      ? CREDENTIAL_TEST_STATUS_FAILED
      : CREDENTIAL_TEST_STATUS_PENDING;
  const activeBasicClientAccess = asArray(readValue(deployment, "BasicClientAccess", "basicClientAccess")).some(isActiveBasicClientAccess);
  const patConfigured = !isBearerMode || tokenSummary.activeTokens.length > 0;
  const basicConfigured = !isBasicMode || activeBasicClientAccess;
  const authenticationConfigured = !hostedApiEnabled || (!isAnonymous && (isBearerMode || isBasicMode) && patConfigured && basicConfigured);
  const patTestSucceeded = !isBearerMode || credentialTestStatus.pat === CREDENTIAL_TEST_STATUS_SUCCESS;
  const basicTestSucceeded = !isBasicMode || credentialTestStatus.basic === CREDENTIAL_TEST_STATUS_SUCCESS;
  const authenticationVerified = hostedApiEnabled && !isAnonymous && (isBearerMode || isBasicMode) && patTestSucceeded && basicTestSucceeded;
  const headerAuthStatus = authenticationVerified && mcpAuthenticationConfigured
    ? "Verified"
    : mcpAuthenticationTestPassed || ((authStatus === "Not configured" || !authStatus) && authenticationConfigured && mcpAuthenticationConfigured)
      ? AUTH_STATUS_CONFIGURED
      : authStatus;
  const authStatusClassName = authStatus === AUTH_STATUS_TEST_FAILED ? "resource-wizard__linked-step-count--error" : "";
  const resultDialogHasDownloadAction = Boolean(resultDialog.downloadAction);

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title={managementMode && mcpEnabled ? "MCP Access" : mcpEnabled ? "Authentication & Authorization" : "Authentication"}
        description={managementMode && mcpEnabled ? "Configure and test this Data Context access policy." : managementMode ? "Configure and test endpoint authentication for this Resource Configuration." : "Optionally configure and test endpoint authentication before validation."}
        countText={headerAuthStatus}
        countClassName={authStatusClassName}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : (
        <>
          <div className="resource-wizard__management-auth-controls">
            {managementMode && !fixedDeploymentId && deploymentOptions.length ? (
              <div className="resource-wizard__management-selector">
                <FieldControl
                  className="resource-wizard__field-control"
                  fieldMetadata={deploymentSelectorField}
                  selectData={deploymentOptions}
                  value={selectedDeploymentValue}
                  disabled={readOnly || saving || busy || deploymentOptions.length < 2}
                  groupId="endpointAuthenticationConfigurator"
                  onChange={selectDeployment}
                  disableError
                />
              </div>
            ) : null}
          </div>
          <div className="resource-wizard__authentication-tab-shell">
            <div className="form-wrapper form-wrapper-tabs-top">
              <div className="form-tabs-container form-tabs-container-top resource-wizard__authentication-tablist" role="tablist" aria-label="Authentication tabs">
                <button
                  id="endpoint-authentication-setup-tab"
                  type="button"
                  className={`form-tab-rows ${activeAuthTab === AUTH_TAB_SETUP ? "form-tab-row-active" : ""}`}
                  role="tab"
                  aria-selected={activeAuthTab === AUTH_TAB_SETUP}
                  aria-controls="endpoint-authentication-tab-panel"
                  onClick={() => selectAuthTab(AUTH_TAB_SETUP)}
                >
                  <span className="form-tab">Setup</span>
                </button>
                <button
                  id="endpoint-authentication-test-tab"
                  type="button"
                  className={`form-tab-rows ${activeAuthTab === AUTH_TAB_TEST ? "form-tab-row-active" : ""}`}
                  role="tab"
                  aria-selected={activeAuthTab === AUTH_TAB_TEST}
                  aria-controls="endpoint-authentication-tab-panel"
                  onClick={() => selectAuthTab(AUTH_TAB_TEST)}
                >
                  <span className="form-tab">Test</span>
                </button>
              </div>
            </div>
            <div
              id="endpoint-authentication-tab-panel"
              className="resource-wizard__review-summary"
              role="tabpanel"
              aria-labelledby={`endpoint-authentication-${activeAuthTab}-tab`}
            >
            {activeAuthTab === AUTH_TAB_SETUP ? (
              <>
                {hostedApiEnabled ? (
                  <section className="resource-wizard__review-summary-section">
              <div className="resource-wizard__review-summary-section-header">
                <div>
                  <h4>API Authentication</h4>
                  <p className="resource-wizard__review-summary-record-subtitle">
                    Select how API callers authenticate to data routes. Basic auth is configured separately because it uses an administrator-issued client key and secret.
                  </p>
                </div>
                <div className="resource-wizard__linked-step-actions-right">
                  <ButtonControl
                    type="standard-small"
                    className="resource-wizard__button resource-wizard__button--secondary"
                    disabled={busy}
                    onClick={() => setModeDialogOpen(true)}
                  >
                    View Auth Examples
                  </ButtonControl>
                </div>
              </div>
              <div className={managementMode ? "resource-wizard__auth-mode-save-row" : "resource-wizard__fields"}>
                <div className="resource-wizard__field">
                  <FieldControl
                    className="resource-wizard__field-control"
                    fieldMetadata={FIELDS.authMode}
                    selectData={AUTH_MODE_OPTIONS}
                    value={form.ApiAuthenticationMode}
                    disabled={readOnly || saving || busy}
                    groupId="endpointAuthenticationConfigurator"
                    onChange={(nextValue) => updateForm("ApiAuthenticationMode", fieldValue(nextValue))}
                    disableError
                  />
                </div>
                {managementMode ? (
                  <ButtonControl
                    type="standard-small"
                    className="resource-wizard__button resource-wizard__button--secondary resource-wizard__auth-mode-save-button"
                    disabled={readOnly || saving || busy || !deployment || !authModeChanged}
                    loading={busy}
                    onClick={() => saveAndContinue()}
                  >
                    Save Auth Mode
                  </ButtonControl>
                ) : null}
              </div>
              {!isAnonymous && isBasicMode ? (
                <div className="resource-wizard__auth-option-grid">
                  <div className="resource-wizard__review-summary-records resource-wizard__auth-option-card">
                    <article className="resource-wizard__review-summary-record">
                      <div className="resource-wizard__review-summary-record-header">
                        <div>
                          <strong>Basic Authentication</strong>
                          <span className="resource-wizard__review-summary-record-subtitle">
                            Request a client key and secret from the administrator. Convoke stores the Azure client key, maps it to a service profile, and grants the endpoint permission. The secret stays in Azure.
                          </span>
                        </div>
                        <div className="resource-wizard__review-summary-record-header-actions">
                          <span className="resource-wizard__review-summary-record-status">
                            {String(form.APIBasicAuthScope || "").trim() ? "Configured" : "Not configured"}
                          </span>
                          <IconControl
                            className="resource-wizard__header-action-icon"
                            iconKey="info"
                            title="How to create Basic client access in Azure"
                            width="1rem"
                            height="1rem"
                            onClick={showBasicClientAccessHelp}
                          />
                          <IconControl
                            className={`resource-wizard__header-action-icon${readOnly || saving || busy ? " disabled" : ""}`}
                            iconKey="edit"
                            title="Edit Basic Authentication"
                            width="1rem"
                            height="1rem"
                            onClick={() => {
                              if (!readOnly && !saving && !busy) {
                                setBasicAccessPanelOpen(true);
                              }
                            }}
                          />
                        </div>
                      </div>
                      <div className="resource-wizard__review-summary-record-facts resource-wizard__review-summary-record-facts--basic-auth">
                        <div className="resource-wizard__info-pair">
                          <span>Caller credential</span>
                          <strong>Client key + secret</strong>
                        </div>
                        <div className="resource-wizard__info-pair">
                          <span>Client ID</span>
                          <strong>{String(form.AzureClientKey || "").trim() || "Not set"}</strong>
                        </div>
                        <div className="resource-wizard__info-pair">
                          <span>Header example</span>
                          <strong>Authorization: Basic base64(clientKey:clientSecret)</strong>
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              ) : null}
              <div className="resource-wizard__review-summary-records">
                <article className="resource-wizard__review-summary-record">
                  <div className="resource-wizard__review-summary-record-header">
                    <div>
                      <strong>{modeDetails.title}</strong>
                        <span className="resource-wizard__review-summary-record-subtitle">
                          {modeDetails.summary}
                        </span>
                      </div>
                  </div>
                  <div className="resource-wizard__review-summary-record-facts">
                    <div className="resource-wizard__info-pair">
                      <span>PAT token</span>
                      <strong>{isBearerMode ? "Enabled" : "Disabled"}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Authentication required</span>
                      <strong>{isAnonymous ? "No" : "Yes"}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Active PAT tokens</span>
                      <strong>{tokenSummary.activeTokens.length}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Revoked or expired</span>
                      <strong>{tokenSummary.inactiveTokens.length}</strong>
                    </div>
                    <div className="resource-wizard__info-pair">
                      <span>Latest token created</span>
                      <strong>{formatDate(tokenSummary.latestCreated)}</strong>
                    </div>
                  </div>
                  {isBearerMode ? (
                    <div className="resource-wizard__pat-token-list">
                      {!tokenSummary.activeTokens.length ? (
                        <div className="resource-wizard__pat-token-row">
                          <div className="resource-wizard__review-summary-record-header">
                            <div>
                              <strong>No active PAT tokens</strong>
                              <span className="resource-wizard__review-summary-record-subtitle">
                                A PAT token is a Personal Access Token created for the current user, scoped to this endpoint permission, and intended for code or scripts that need API access without signing in to the app.
                              </span>
                            </div>
                            <div className="resource-wizard__review-summary-record-header-actions">
                              <span className="resource-wizard__review-summary-record-status">Not configured</span>
                              <IconControl
                                className={`resource-wizard__header-action-icon${readOnly || saving || busy ? " disabled" : ""}`}
                                iconKey="create"
                                title="Create PAT Token"
                                width="1rem"
                                height="1rem"
                                onClick={readOnly || saving || busy ? undefined : () => setTokenDialogOpen(true)}
                              />
                            </div>
                          </div>
                        </div>
                      ) : null}
                      {tokenSummary.displayTokens.length ? tokenSummary.displayTokens.map((token) => (
                        <div className="resource-wizard__pat-token-row" key={String(getTokenId(token) || readValue(token, "TokenLabel", "tokenLabel"))}>
                          <div className="resource-wizard__review-summary-record-header">
                            <div>
                              <strong>{getTokenRowTitle(token)}</strong>
                            </div>
                            <div className="resource-wizard__review-summary-record-header-actions">
                              <span className="resource-wizard__review-summary-record-status">{getTokenStatusLabel(token)}</span>
                              {isActiveToken(token) ? (
                                <IconControl
                                  className={`resource-wizard__header-action-icon resource-wizard__header-action-icon--danger${readOnly || saving || busy ? " disabled" : ""}`}
                                  iconKey="delete"
                                  title={`Revoke PAT Token ${readValue(token, "TokenLabel", "tokenLabel") || ""}`.trim()}
                                  width="1rem"
                                  height="1rem"
                                  onClick={readOnly || saving || busy ? undefined : () => revokeToken(token)}
                                />
                              ) : null}
                            </div>
                          </div>
                          <div className="resource-wizard__review-summary-record-facts">
                            <div className="resource-wizard__info-pair">
                              <span>Permission</span>
                              <strong>{readValue(token, "PermissionKey", "permissionKey") || "Not set"}</strong>
                            </div>
                            <div className="resource-wizard__info-pair">
                              <span>Expiration</span>
                              <strong>{formatDate(readValue(token, "ExpirationDate", "expirationDate"))}</strong>
                            </div>
                            <div className="resource-wizard__info-pair">
                              <span>Last used</span>
                              <strong>{formatDate(readValue(token, "LastUsedDate", "lastUsedDate"))}</strong>
                            </div>
                            <div className="resource-wizard__info-pair">
                              <span>Fingerprint</span>
                              <strong>{readValue(token, "TokenFingerprint", "tokenFingerprint") || "Not set"}</strong>
                            </div>
                          </div>
                        </div>
                      )) : null}
                    </div>
                  ) : null}
                </article>
              </div>
              {isAnonymous ? (
                <div className="resource-wizard__connection-status failed">
                  Anonymous API access allows callers to reach enabled API routes without PAT tokens or Basic credentials.
                </div>
              ) : null}
                  </section>
                ) : null}
                {mcpEnabled ? (
                  <section className="resource-wizard__review-summary-section">
                <div className="resource-wizard__review-summary-record-header">
                  <h4>MCP Access</h4>
                </div>
                <div className="resource-wizard__fields">
                  <div className="resource-wizard__field">
                    <FieldControl
                      className="resource-wizard__field-control"
                      fieldMetadata={FIELDS.mcpIdentityMode}
                      selectData={MCP_IDENTITY_MODE_OPTIONS}
                      value={form.McpExecutionIdentityMode}
                      disabled={readOnly || saving || busy}
                      groupId="endpointAuthenticationConfigurator"
                      onChange={(nextValue) => updateForm("McpExecutionIdentityMode", fieldValue(nextValue))}
                      disableError
                    />
                  </div>
                  <div className="resource-wizard__field">
                    <FieldControl
                      className="resource-wizard__field-control"
                      fieldMetadata={FIELDS.mcpAuthorizationMode}
                      selectData={MCP_AUTHORIZATION_MODE_OPTIONS}
                      value={form.McpAuthorizationMode}
                      disabled={readOnly || saving || busy}
                      groupId="endpointAuthenticationConfigurator"
                      onChange={(nextValue) => updateForm("McpAuthorizationMode", fieldValue(nextValue))}
                      disableError
                    />
                  </div>
                  {Number(form.McpAuthorizationMode) === MCP_AUTH_MODE_SELECTED_PROFILES ? (
                    <div className="resource-wizard__field">
                      <FieldControl
                        className="resource-wizard__field-control"
                        fieldMetadata={FIELDS.mcpAuthorizedProfiles}
                        selectData={visibleProfileOptions}
                        value={form.McpAuthorizedProfileIds}
                        disabled={readOnly || saving || busy || profilesLoading}
                        groupId="endpointAuthenticationConfigurator"
                        onChange={(nextValue) => updateForm("McpAuthorizedProfileIds", normalizeProfileIds(nextValue))}
                        disableError
                      />
                    </div>
                  ) : null}
                </div>
                  </section>
                ) : null}
              </>
            ) : (
              <section className="resource-wizard__review-summary-section">
              <h4>Test Authentication</h4>
              <div className="resource-wizard__auth-option-grid">
                {isBearerMode ? (
                  <article className="resource-wizard__review-summary-record resource-wizard__auth-option-card resource-wizard__auth-test-card">
                    <div className="resource-wizard__review-summary-record-header">
                      <div />
                      <span className={`resource-wizard__credential-test-status resource-wizard__credential-test-status--${getCredentialTestStatusClass(credentialTestStatus.pat)}`}>
                        {credentialTestStatus.pat}
                      </span>
                    </div>
                    <div className="resource-wizard__fields">
                      <div className="resource-wizard__field">
                        <FieldControl
                          className="resource-wizard__field-control"
                          fieldMetadata={FIELDS.patTestCredential}
                          value={testCredentials.PatTestCredential}
                          disabled={readOnly || saving || busy}
                          groupId="endpointAuthenticationConfigurator"
                          onChange={(nextValue) => updateTestCredentialField("PatTestCredential", fieldValue(nextValue))}
                          disableError
                        />
                      </div>
                    </div>
                    <div className="resource-wizard__linked-step-actions">
                      <div />
                      <div className="resource-wizard__linked-step-actions-right">
                        <ButtonControl
                          type="standard-small"
                          className="resource-wizard__button resource-wizard__button--secondary"
                          disabled={readOnly || saving || busy || !String(testCredentials.PatTestCredential || "").trim()}
                          loading={busy}
                          onClick={() => runHostedApiAuthenticationTest({
                            authType: "BearerToken",
                            credential: testCredentials.PatTestCredential,
                            title: "PAT Token Test Result",
                            testStatusKey: "pat"
                          })}
                        >
                          Test PAT
                        </ButtonControl>
                      </div>
                    </div>
                  </article>
                ) : null}
                {isBasicMode ? (
                  <article className="resource-wizard__review-summary-record resource-wizard__auth-option-card resource-wizard__auth-test-card">
                    <div className="resource-wizard__review-summary-record-header">
                      <div>
                        <strong>Basic Client Test</strong>
                        <span className="resource-wizard__review-summary-record-subtitle">
                          Test a client key and secret using a generated Basic authorization header.
                        </span>
                      </div>
                      <span className={`resource-wizard__credential-test-status resource-wizard__credential-test-status--${getCredentialTestStatusClass(credentialTestStatus.basic)}`}>
                        {credentialTestStatus.basic}
                      </span>
                    </div>
                    <div className="resource-wizard__fields">
                      <div className="resource-wizard__field">
                        <FieldControl
                          className="resource-wizard__field-control"
                          fieldMetadata={FIELDS.basicTestClientKey}
                          value={testCredentials.BasicTestClientKey}
                          disabled={readOnly || saving || busy}
                          groupId="endpointAuthenticationConfigurator"
                          onChange={(nextValue) => updateTestCredentialField("BasicTestClientKey", fieldValue(nextValue))}
                          disableError
                        />
                      </div>
                      <div className="resource-wizard__field">
                        <FieldControl
                          className="resource-wizard__field-control"
                          fieldMetadata={FIELDS.basicTestClientSecret}
                          value={testCredentials.BasicTestClientSecret}
                          disabled={readOnly || saving || busy}
                          groupId="endpointAuthenticationConfigurator"
                          onChange={(nextValue) => updateTestCredentialField("BasicTestClientSecret", fieldValue(nextValue))}
                          disableError
                        />
                      </div>
                    </div>
                    <div className="resource-wizard__linked-step-actions">
                      <div />
                      <div className="resource-wizard__linked-step-actions-right">
                        <ButtonControl
                          type="standard-small"
                          className="resource-wizard__button resource-wizard__button--secondary"
                          disabled={
                            readOnly ||
                            saving ||
                            busy ||
                            !String(testCredentials.BasicTestClientKey || "").trim() ||
                            !String(testCredentials.BasicTestClientSecret || "").trim()
                          }
                          loading={busy}
                          onClick={() => runHostedApiAuthenticationTest({
                            authType: "BasicAuth",
                            credential: buildBasicAuthorizationHeader(testCredentials.BasicTestClientKey, testCredentials.BasicTestClientSecret),
                            title: "Basic Client Test Result",
                            testStatusKey: "basic"
                          })}
                        >
                          Test Basic
                        </ButtonControl>
                      </div>
                    </div>
                  </article>
                ) : null}
                {mcpEnabled ? (
                  <article
                    className="resource-wizard__auth-test-card resource-wizard__auth-test-card--flat"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div className="resource-wizard__review-summary-record-header">
                      <div>
                        <strong>MCP OAuth Test</strong>
                        <span className="resource-wizard__review-summary-record-subtitle">
                          Test an MCP OAuth token against the configured MCP endpoint surface.
                        </span>
                      </div>
                      <span className={`resource-wizard__credential-test-status resource-wizard__credential-test-status--${getCredentialTestStatusClass(mcpTestStatus)}`}>
                        {mcpTestStatus}
                      </span>
                    </div>
                    <div className="resource-wizard__fields">
                      <div className="resource-wizard__field">
                        <FieldControl
                          className="resource-wizard__field-control"
                          fieldMetadata={FIELDS.mcpTestCredential}
                          value={testCredentials.McpTestCredential}
                          disabled={readOnly || saving || busy}
                          groupId="endpointAuthenticationConfigurator"
                          onChange={(nextValue) => updateTestCredentialField("McpTestCredential", fieldValue(nextValue))}
                          disableError
                        />
                      </div>
                    </div>
                    <div className="resource-wizard__linked-step-actions">
                      <div />
                      <div className="resource-wizard__linked-step-actions-right">
                        <button
                          type="button"
                          className={`btn-control btn-control__standard-small resource-wizard__button resource-wizard__button--secondary${readOnly || saving || busy ? " disabled" : ""}`}
                          disabled={readOnly || saving || busy}
                          onClick={runMcpAuthenticationTest}
                        >
                          Test MCP
                        </button>
                      </div>
                    </div>
                  </article>
                ) : null}
                {isAnonymous ? (
                  <article className="resource-wizard__review-summary-record resource-wizard__auth-option-card resource-wizard__auth-test-card">
                    <div className="resource-wizard__review-summary-record-header">
                      <div>
                        <strong>Anonymous Route Test</strong>
                        <span className="resource-wizard__review-summary-record-subtitle">
                          Test route access without sending credentials.
                        </span>
                      </div>
                    </div>
                    <div className="resource-wizard__linked-step-actions">
                      <div />
                      <div className="resource-wizard__linked-step-actions-right">
                        <ButtonControl
                          type="standard-small"
                          className="resource-wizard__button resource-wizard__button--secondary"
                          disabled={readOnly || saving || busy}
                          loading={busy}
                          onClick={() => runAuthenticationTest({
                            authType: "Anonymous",
                            credential: "",
                            title: "Anonymous Route Test Result"
                          })}
                        >
                          Test Anonymous
                        </ButtonControl>
                      </div>
                    </div>
                  </article>
                ) : null}
              </div>
              </section>
            )}
          </div>
          </div>
          <div className="resource-wizard__linked-step-actions">
            <div className="resource-wizard__linked-step-actions-left">
              {!managementMode ? (
                <ButtonControl
                  type="standard-small"
                  className="resource-wizard__button resource-wizard__button--secondary"
                  disabled={readOnly || saving || busy}
                  onClick={skipAuthentication}
                >
                  Skip Authentication Setup
                </ButtonControl>
              ) : null}
            </div>
            <div className="resource-wizard__linked-step-actions-right">
              {!managementMode ? (
                <ButtonControl
                  type="standard-small"
                  className="resource-wizard__button resource-wizard__button--secondary"
                  disabled={readOnly || saving || busy}
                  onClick={() => saveAuthConfiguration(AUTH_STATUS_CONFIGURED).catch((ex) => setLocalError(getErrorSummary(ex, "Authentication setup could not be saved.")))}
                >
                  Save
                </ButtonControl>
              ) : null}
              {!managementMode ? (
                <ButtonControl
                  type="ok-small"
                  className="resource-wizard__button resource-wizard__button--primary"
                  disabled={readOnly || saving || busy}
                  loading={saving || busy}
                  onClick={() => saveAndContinue()}
                >
                  Save & Continue
                </ButtonControl>
              ) : null}
            </div>
          </div>
        </>
      )}
      <SidePanelFormControl
        key={`endpoint-basic-client-access-${basicAccessPanelOpen ? "open" : "closed"}`}
        open={basicAccessPanelOpen}
        title="Basic Client Access"
        metadata={BASIC_CLIENT_ACCESS_METADATA}
        formFields={BASIC_CLIENT_ACCESS_METADATA.Fields}
        itemData={{
          AzureClientKey: form.AzureClientKey,
          FriendlyClientName: form.FriendlyClientName,
          APIBasicAuthScope: basicAccessPanelScope
        }}
        onSave={saveBasicClientAccess}
        onCancel={() => setBasicAccessPanelOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? "Saving..." : "Save Client Access"}
        setOpenEditSidePanel={(value) => setBasicAccessPanelOpen(Boolean(value))}
        setItemData={setBasicAccessItemData}
        viewOnly={readOnly || saving || busy}
      />
      <DialogControl
        openDialog={anonymousDialogOpen}
        title="Anonymous API Access"
        okText="Continue With Anonymous Access"
        cancelText="Cancel"
        onOk={() => {
          setAnonymousDialogOpen(false);
          saveAndContinue({ skipAnonymousWarning: true });
        }}
        onCancel={() => setAnonymousDialogOpen(false)}
      >
        <p>Anonymous API access allows callers to reach enabled API routes without PAT tokens or Basic credentials.</p>
      </DialogControl>
      <DialogControl
        openDialog={modeDialogOpen}
        title="API Authentication Modes"
        okText="Close"
        onOk={() => setModeDialogOpen(false)}
        onCancel={() => setModeDialogOpen(false)}
        hideCancel={true}
      >
        <div className="resource-wizard__endpoint-route-list">
          {authContext.errorMessage ? (
            <div className="resource-wizard__connection-status failed">
              {authContext.errorMessage}
            </div>
          ) : null}
          <article className="resource-wizard__endpoint-route">
            <div className="resource-wizard__endpoint-route-header">
              <strong>{modeDetails.title}</strong>
              <span>{modeDetails.status}</span>
            </div>
            <p>{modeDetails.summary}</p>
            {modeDetails.notes.map((note) => (
              <p key={note}>{note}</p>
            ))}
            {authExampleLines.length ? <code>{authExampleLines.join("\n")}</code> : null}
          </article>
          <article className="resource-wizard__endpoint-route">
            <div className="resource-wizard__endpoint-route-header">
              <strong>Basic Authentication</strong>
              <span>{String(form.APIBasicAuthScope || "").trim() ? "Configured" : "Optional"}</span>
            </div>
            <p>Basic auth is not a username/password form in setup. The caller must request a client key and secret from the administrator.</p>
            <p>Configure Basic Auth Scope when this endpoint should accept Basic credentials and exchange them through the auth manager.</p>
            {basicAuthExampleLines.length ? <code>{basicAuthExampleLines.join("\n")}</code> : null}
          </article>
          {mcpEnabled ? (
            <article className="resource-wizard__endpoint-route">
              <div className="resource-wizard__endpoint-route-header">
                <strong>MCP OAuth</strong>
                <span>OAuth only</span>
              </div>
              <p>MCP does not support anonymous access. MCP clients authenticate with OAuth scopes and the configured MCP identity mode.</p>
              {mcpAuthExampleLines.length ? <code>{mcpAuthExampleLines.join("\n")}</code> : null}
            </article>
          ) : null}
        </div>
      </DialogControl>
      <DialogControl
        openDialog={tokenDialogOpen}
        title="Create PAT Token"
        okText="Create PAT Token"
        cancelText="Cancel"
        onOk={createToken}
        onCancel={() => setTokenDialogOpen(false)}
        disableOk={!String(tokenForm.TokenLabel || "").trim() || busy}
      >
        <div className="resource-wizard__fields">
          <div className="resource-wizard__field">
            <FieldControl
              className="resource-wizard__field-control"
              fieldMetadata={FIELDS.tokenLabel}
              value={tokenForm.TokenLabel}
              disabled={busy}
              groupId="endpointAuthenticationTokenDialog"
              onChange={(nextValue) => setTokenForm((previous) => ({ ...previous, TokenLabel: fieldValue(nextValue) }))}
              disableError
            />
          </div>
          <div className="resource-wizard__field">
            <FieldControl
              className="resource-wizard__field-control"
              fieldMetadata={FIELDS.tokenExpiration}
              selectData={FIELDS.tokenExpiration.DataJson}
              value={tokenForm.ExpirationDuration}
              disabled={busy}
              groupId="endpointAuthenticationTokenDialog"
              onChange={(nextValue) => setTokenForm((previous) => ({ ...previous, ExpirationDuration: fieldValue(nextValue) }))}
              disableError
            />
          </div>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={resultDialog.open}
        title={resultDialog.title}
        okText={resultDialogHasDownloadAction ? "Generate Azure setup script" : "Close"}
        cancelText="Cancel"
        onOk={resultDialogHasDownloadAction
          ? (resultDialog.downloadAction?.kind === "mcpOAuth" ? downloadMcpOAuthSetupScript : downloadBasicClientAccessScript)
          : closeResultDialog}
        onCancel={closeResultDialog}
        hideCancel={!resultDialogHasDownloadAction}
        disableOk={resultDialog.downloadAction?.disabled}
        loadingOk={busy}
      >
        {resultDialogHasDownloadAction && resultDialog.downloadStatus ? (
          <div className="resource-wizard__connection-status">
            {resultDialog.downloadStatus}
          </div>
        ) : null}
        {resultDialog.html ? (
          <div
            className="resource-wizard__endpoint-result-html"
            dangerouslySetInnerHTML={{ __html: resultDialog.html }}
          />
        ) : (
          <pre className="resource-wizard__endpoint-status-json">{resultDialog.content}</pre>
        )}
        {resultDialog.copyValue ? (
          <div className="resource-wizard__endpoint-result-actions">
            <ButtonControl
              type="standard-small"
              className="resource-wizard__button resource-wizard__button--secondary"
              onClick={copyResultDialogValue}
            >
              {resultDialog.copyLabel || "Copy"}
            </ButtonControl>
            {resultDialog.copyStatus ? <span>{resultDialog.copyStatus}</span> : null}
          </div>
        ) : null}
      </DialogControl>
    </div>
  );
};

export default EndpointAuthenticationConfiguratorStep;
